<template>
  <div class="page-content campaign-edit" :class="{ wide_page: form.offer_id && !clone }">
    <b-loading :active.sync="isLoadingOrSaving" :can-cancel="false"></b-loading>
    <div v-show="utcTipOpen" class="utc-tip" :style="utcTipStyle" role="tooltip">
      {{ $t('impressions_today_utc_tooltip') }}
    </div>

    <header class="campaign-header" v-if="form.offer_id && !clone">
      <div>
        <h1 class="campaign-title">{{ form.name || $t('campaign_info') }}</h1>
        <div class="campaign-header-meta">
          <span v-if="platformName" class="meta-chip">{{ platformName }}</span>
          <span v-if="form.app_identifier" class="meta-app">{{ form.app_identifier }}</span>
          <span class="status-pill" :class="'status-' + (form.status || 'active')">{{ form.status }}</span>
        </div>
      </div>
      <div class="campaign-header-links">
        <a :href="campaignReportUrl">{{ $t('campaign_report') }}</a>
        <a :href="pieReportUrl">{{ $t('post_install_reports') }}</a>
        <a href="campaign_api.html">{{ $t('campaign_api') }}</a>
      </div>
    </header>
    <h1 class="title" v-else>{{ clone ? $t('clone_campaign') : $t('campaign_info') }}</h1>

    <div class="campaign-layout" :class="{ 'has-rail': form.offer_id && !clone }">
    <div class="campaign-main">
        <form id="campaignForm">
          <div class="card-pair">
          <section class="ds-card" :class="{ 'is-collapsed': !openBasics }">
            <button type="button" class="card-head" @click="openBasics = !openBasics">
              <span class="card-head-main">
                <img v-if="!openBasics && campaign.thumbnail_url" class="card-logo" :src="campaign.thumbnail_url" alt="">
                <span class="card-head-text">
                  <span class="ds-card-title">{{ $t('campaign_basics') }}</span>
                  <span v-if="!openBasics" class="card-summary">{{ basicsSummary }}</span>
                </span>
              </span>
              <span class="card-chevron" :class="{ 'is-open': openBasics }"></span>
            </button>
            <div class="card-body" :class="{ 'is-open': openBasics }"><div class="card-body-inner">
          <b-field>
            <template v-slot:label>
              {{ $t('campaign_name') }} *
              <b-tooltip type="is-dark" :label="$t('campaign_name_tooltip')" multilined>
                <b-icon size="is-small" icon="help-circle-outline"></b-icon>
              </b-tooltip>
            </template>
            <b-input v-model="form.name" :placeholder="$t('campaign_name')"
                     :disabled="initialStatus === 'deleted' && !clone" type="text" required></b-input>
          </b-field>

          <div v-if="form.total_budget_check === false && !clone" class="inline box dark-gray yellow-background">
            {{ $t('campaign_reached_total_budget_1') }} {{ form.total_budget | toCurrency }}.
            {{ $t('campaign_reached_total_budget_2') }}
          </div>

          <div v-if="form.total_budget75_percent_check === false && !clone"
               class="inline box dark-gray yellow-background">
            {{ $t('campaign_reached_75_percent_total_budget_1') }} {{ form.total_budget | toCurrency }}.
            {{ $t('campaign_reached_75_percent_total_budget_2') }}
          </div>

          <div v-if="form.daily_install_cap_check === false && !clone" class="inline box dark-gray yellow-background">
            {{ $t('campaign_reached_daily_install_cap_1') }} {{ form.daily_install_cap }}
            {{ $t('campaign_reached_daily_install_cap_2') }}
          </div>

          <div class="platform-app-row">
            <b-field>
              <template v-slot:label>
                {{ $t('platform') }} *
                <b-tooltip type="is-dark" :label="$t('platform_tooltip')" multilined>
                  <b-icon size="is-small" icon="help-circle-outline"></b-icon>
                </b-tooltip>
              </template>
              <div class="control">
                <span class="select">
                  <select v-model="form.platform_id" v-on:change="updatePlatform">
                    <option v-for="platform in platforms"
                            :value="platform.id"
                            :key="platform.id"
                            :selected="platform.id === form.platform_id"
                            :disabled="initialStatus === 'deleted' && !clone">
                      {{ platform.name }}
                    </option>
                  </select>
                </span>
              </div>
            </b-field>
            <b-field class="stacked-label app-id-field">
              <template v-slot:label>
                <span class="field-title">{{ $t('package_name_short') }} *</span>
                <b-tooltip type="is-dark" :label="$t('package_name_tooltip')" multilined>
                  <b-icon size="is-small" icon="help-circle-outline"></b-icon>
                </b-tooltip>
                <span class="field-hint">{{ $t('package_name_hint') }}</span>
              </template>
              <b-input v-model="form.app_identifier" :placeholder="$t('package_name_hint')"
                       :disabled="initialStatus === 'deleted' && !clone"
                       :readonly="form.impressions_ltd > 0 && !clone" v-on:blur="parseAppIdentifier" type="text"
                       required class="app-textbox"></b-input>
            </b-field>
            <img v-if="campaign.thumbnail_url" :src="campaign.thumbnail_url" alt="" class="thumbnail">
            <img v-else src="icons/app.png" alt="" class="thumbnail">
          </div>

          <b-field v-if="form.platform_id === 3">
            <template v-slot:label>
              {{ $t('app_platform_to_target') }} *
              <b-tooltip type="is-dark" :label="$t('app_platform_to_target_tooltip')" multilined>
                <b-icon size="is-small" icon="help-circle-outline"></b-icon>
              </b-tooltip>
            </template>
            <div class="control">
              <span class="select">
                <select v-model="form.ctv_target">
                  <option v-for="platform in ctvTarget"
                          :value="platform.id"
                          :key="platform.id"
                          :selected="platform.id === form.platform_id">
                    {{ platform.name }}
                  </option>
                </select>
              </span>
            </div>
          </b-field>
          <ErrorMessages :errors="app_errors"></ErrorMessages>

          <div class="field-row">
            <div class="inline status-dates">
              <b-field v-if="form.status === 'active' && form.impression_overrun">
                <template v-slot:label>
                  {{ $t('status') }}
                </template>
                <div class="control red">
                  {{ $t('impression_overrun') }} <a href="support.html" target="_">{{ $t('contact_us') }}</a>
                  {{ $t('resume_campaign') }}.
                </div>
              </b-field>

              <b-field v-else>
                <template v-slot:label>
                  {{ $t('status') }} *
                  <b-tooltip type="is-dark" :label="$t('campaign_status_tooltip')" multilined>
                    <b-icon size="is-small" icon="help-circle-outline"></b-icon>
                  </b-tooltip>
                </template>
                <div class="control">
                  <span class="select">
                      <select v-model="form.status">
                          <option v-for="status in statuses"
                                  :value="status.id"
                                  :key="status.id"
                                  :selected="status.id === form.status"
                                  :disabled="initialStatus === 'deleted' && !clone">
                              {{ status.name }}
                          </option>
                      </select>
                  </span>
                </div>
              </b-field>

              <b-field class="left-spacer-30">
                <template v-slot:label>
                  {{ $t('start_date') }}
                  <b-tooltip type="is-dark" :label="$t('start_date_tooltip')" multilined>
                    <b-icon size="is-small" icon="help-circle-outline"></b-icon>
                  </b-tooltip>
                </template>
                <div class="inline control">
                  <b-datepicker v-model="form.start_date" :date-formatter="formatDate" class="date-picker" trap-focus
                                :disabled="initialStatus === 'deleted' && !clone"></b-datepicker>
                  <span class="icon is-right is-clickable left-spacer-5 top-spacer-5"
                        @click.prevent="initialStatus === 'deleted' && !clone || clearStartDate()"><i
                      class="mdi mdi-close-circle mdi-24px"></i></span>
                </div>
              </b-field>

              <b-field class="left-spacer-30">
                <template v-slot:label>
                  {{ $t('end_date') }}
                  <b-tooltip type="is-dark" :label="$t('end_date_tooltip')" multilined>
                    <b-icon size="is-small" icon="help-circle-outline"></b-icon>
                  </b-tooltip>
                </template>
                <div class="inline control">
                  <b-datepicker v-model="form.end_date" :date-formatter="formatDate" class="date-picker" trap-focus
                                :disabled="initialStatus === 'deleted' && !clone"></b-datepicker>
                  <span class="icon is-right is-clickable left-spacer-5 top-spacer-5"
                        @click.prevent="initialStatus === 'deleted' && !clone || clearEndDate()"><i
                      class="mdi mdi-close-circle mdi-24px"></i></span>
                </div>
              </b-field>
            </div>

            <div v-if="showPauseMessage()" class="bottom-spacer-20">
              {{ $t('pause_message') }}
            </div>

            <div v-if="askForPauseReason()">
              <b-field>
                <template v-slot:label>
                  {{ $t(form.status + '_reason') }} *
                  <b-tooltip type="is-dark" :label="$t('campaign_' + form.status + '_feedback_tooltip')" multilined>
                    <b-icon size="is-small" icon="help-circle-outline"></b-icon>
                  </b-tooltip>
                </template>
                <div class="control">
                                    <span class="select">
                                        <select v-model="form.pause_reason" required>
                                            <option v-for="reason in pauseFeedbackReasons"
                                                    :value="reason.id"
                                                    :key="reason.id"
                                                    :selected="reason.id === form.pause_reason">
                                                {{ reason.name }}
                                            </option>
                                        </select>
                                    </span>
                </div>
              </b-field>

              <b-field>
                <template v-slot:label>
                  {{ $t('note') }}
                </template>
                <div class="control">
                  <b-input v-model="form.pause_reason_note" type="textarea" maxlength="1000"
                           :disabled="initialStatus === 'deleted' && !clone" class="wrap-text"></b-input>
                </div>
              </b-field>
            </div>
          </div>

          <div class="billing-row">
          <b-field v-if="(form.impressions_ltd === 0 || form.impressions_ltd === null) || clone">
            <template slot="label">
              {{$t('billing_type')}}
              <b-tooltip type="is-dark" :label="$t('billing_type_tooltip')" multilined>
                <b-icon size="is-small" icon="help-circle-outline"></b-icon>
              </b-tooltip>
            </template>
            <div class="control billing-control">
              <span class="select">
                  <select v-model="form.billing_type_id" :disabled="initialStatus === 'deleted' && !clone" v-on:change="updatePricing">
                      <option v-for="billingType in billingTypes"
                              :value="billingType.id"
                              :key="billingType.id"
                              :selected="billingType.id === form.billing_type_id">
                          {{billingType.name}}
                      </option>
                  </select>
              </span>
              <div v-if="form.billing_type_id === BillingTypeEnum.CPM.id" class="billing-note">
                {{ $t('cpm_postback_hint') }}
                <a href="cpm_postback.html">{{ $t('cpm_postback_link') }}</a>
              </div>
            </div>
          </b-field>

          <b-field v-else>
            <template v-slot:label>
              {{ $t('billing_type') }}
              <b-tooltip v-if="form.billing_type_id === BillingTypeEnum.CPI.id" type="is-dark" :label="$t('cpi_billing_type_tooltip')"
                         multilined>
                <b-icon size="is-small" icon="help-circle-outline"></b-icon>
              </b-tooltip>
              <b-tooltip v-else type="is-dark" :label="$t('cpa_billing_type_tooltip')" multilined>
                <b-icon size="is-small" icon="help-circle-outline"></b-icon>
              </b-tooltip>
            </template>
            <div class="billing-control">
              <span class="billing-value" v-if="form.billing_type_id === BillingTypeEnum.CPI.id">CPI</span>
              <span class="billing-value" v-else-if="form.billing_type_id === BillingTypeEnum.CPM.id">CPM</span>
              <span class="billing-value" v-else>CPA</span>
              <div v-if="form.billing_type_id === BillingTypeEnum.CPM.id" class="billing-note">
                {{ $t('cpm_postback_hint') }}
                <a href="cpm_postback.html">{{ $t('cpm_postback_link') }}</a>
              </div>
            </div>
          </b-field>
          </div>

          <b-field v-if="form.billing_type_id === BillingTypeEnum.CPI.id">
            <template v-slot:label>
              {{ $t('cost_per_install') }} *
              <b-tooltip type="is-dark" :label="$t('cost_per_install_tooltip')" multilined>
                <b-icon size="is-small" icon="help-circle-outline"></b-icon>
              </b-tooltip>
            </template>

            <div>
              <div class="bottom-spacer-20">{{ $t('cost_per_install_event_optimization') }}</div>
              <b-input id="cost_per_install_field" icon="currency-usd" v-model="form.payout"
                       :disabled="initialStatus === 'deleted' && !clone" :placeholder="$t('cost_per_install')"
                       type="number" min="0.05" max="100" step="0.01" required></b-input>
            </div>
          </b-field>
          <template v-else-if="form.billing_type_id === BillingTypeEnum.CPM.id">
            <b-field>
              <template v-slot:label>
                {{ $t('cost_per_mile') }} *
                <b-tooltip type="is-dark" :label="$t('cost_per_mile_tooltip')" multilined>
                  <b-icon size="is-small" icon="help-circle-outline"></b-icon>
                </b-tooltip>
              </template>
              <b-input
                  id="cost_per_mile_field"
                  icon="currency-usd"
                  v-model="form.payout"
                  :disabled="initialStatus === 'deleted' && !clone"
                  :placeholder="$t('cost_per_mile')"
                  type="number"
                  min="0.05"
                  max="100"
                  step="0.01"
                 
                  required>
              </b-input>
            </b-field>

            <b-field>
              <template v-slot:label>
                {{ $t('maximum_cpm') }}
              </template>
              <b-input
                  id="maximum_cpm_field"
                  icon="currency-usd"
                  v-model="form.maximum_cpm"
                  :disabled="initialStatus === 'deleted' && !clone"
                  :placeholder="$t('maximum_cpm')"
                  type="number"
                  min="0.00"
                  max="100"
                  step="0.01"
                 >
              </b-input>
            </b-field>

            <button type="button" class="disclosure-row" @click="maximumCpmAdvancedOpen = !maximumCpmAdvancedOpen">
              <span>{{ $t('advanced') }}</span>
              <span class="card-chevron" :class="{ 'is-open': maximumCpmAdvancedOpen }"></span>
            </button>
            <div class="disclosure-body" :class="{ 'is-open': maximumCpmAdvancedOpen }">
              <div class="disclosure-body-inner">
                <p class="imp-type-max-help">{{ $t('maximum_cpm_by_impression_type_tooltip') }}</p>
                <div class="imp-type-max-grid">
                  <label v-for="t in impressionTypeOptions" :key="'max-cpm-' + t.id" class="imp-type-max">
                    <span>{{ t.name }}</span>
                    <b-input
                        :id="'maximum_cpm_type_' + t.id"
                        v-model="impressionTypeMaximumCpms[t.id]"
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        :disabled="initialStatus === 'deleted' && !clone"
                        placeholder="—"
                        @blur="clearIgnoredImpressionTypeMaximumCpm(t.id)">
                    </b-input>
                  </label>
                </div>
              </div>
            </div>
          </template>
          <b-field v-else-if="form.billing_type_id !== 5">
            <template v-slot:label>
              {{ $t('cost_per_acquisition') }} *
              <b-tooltip type="is-dark" :label="$t('cost_per_acquisition_tooltip')" multilined>
                <b-icon size="is-small" icon="help-circle-outline"></b-icon>
              </b-tooltip>
            </template>
            <b-input id="cost_per_acquisition_field" icon="currency-usd" v-model="form.payout"
                     :disabled="initialStatus === 'deleted' && !clone" :placeholder="$t('cost_per_acquisition')"
                     type="number" min="0.10" max="500" step="0.01" required></b-input>
          </b-field>

          <div
              v-if="(pricing.min_price && !form.offer_id) || (form.suggested_payout && form.suggested_payout > form.payout) || (pricing.suggested_price && pricing.suggested_price > form.payout) || (video_pricing.suggested_price && video_pricing.suggested_price > form.payout)"
              class="inline dark-gray">
            <div v-if="pricing.min_price && !form.offer_id" class="bottom-spacer-20 right-spacer-40">
              <b>{{ $t('min_price') }}:</b> {{ pricing.min_price | toCurrency }}
            </div>
            <div v-if="form.suggested_payout && form.suggested_payout > form.payout"
                 class="bottom-spacer-20 right-spacer-40"><b>{{ $t('suggested_price') }}: </b>
              <span v-if="form.impact >= 0.5 && !clone" class="red">{{ form.suggested_payout | toCurrency }}</span>
              <span v-else>{{ form.suggested_payout | toCurrency }}</span>
            </div>
            <div v-else-if="pricing.suggested_price && pricing.suggested_price > form.payout"
                 class="bottom-spacer-20 right-spacer-40"><b>{{ $t('suggested_price') }}: </b>
              <span>{{ pricing.suggested_price | toCurrency }}</span>
            </div>
            <div v-if="video_pricing.suggested_price && video_pricing.suggested_price > form.payout"
                 class="bottom-spacer-20 right-spacer-40"><b>{{ $t('suggested_video_price') }}: </b>
              <span>{{ video_pricing.suggested_price | toCurrency }}</span>
            </div>
            <div v-if="form.impact && form.suggested_payout && form.suggested_payout > form.payout && !clone"
                 class="bottom-spacer-20"><b>{{ $t('impact') }}: </b>
              <span v-if="form.impact >= 0.5" class="red">{{ form.impact | toPercent }} </span>
              <span v-else>{{ form.impact | toPercent }} </span>
              <b-tooltip type="is-dark" :label="$t('impact_tooltip')" multilined>
                <b-icon size="is-small" icon="help-circle-outline"></b-icon>
              </b-tooltip>
            </div>
            <div v-if="isSuperUser && form.suggested_payout_recommendation_engine" class="bottom-spacer-20">
              {{ $t('recommendation_based_on') }} <span>{{ form.suggested_payout_recommendation_engine }}</span>
            </div>
            <div v-if="payoutBelowSuggestion && !clone" class="cpi-hint">
              {{ $t('cpi_below_suggested') }}
            </div>
          </div>

            </div></div>
          </section>

          <section class="ds-card" :class="{ 'is-collapsed': !openTargeting }">
            <button type="button" class="card-head" @click="openTargeting = !openTargeting">
              <span class="card-head-text">
                <span class="ds-card-title">{{ $t('targeting_delivery') }}</span>
                <span v-if="!openTargeting" class="card-summary">{{ targetingSummary }}</span>
              </span>
              <span class="card-chevron" :class="{ 'is-open': openTargeting }"></span>
            </button>
            <div class="card-body" :class="{ 'is-open': openTargeting }"><div class="card-body-inner">

          <b-field class="countries-field">
            <template v-slot:label>
              {{ $t('targeted_countries') }} *
              <b-tooltip type="is-dark" :label="$t('targeted_countries_tooltip')" multilined>
                <b-icon size="is-small" icon="help-circle-outline"></b-icon>
              </b-tooltip>
            </template>
            <div class="control">
              <b-taginput
                  v-model="form.countries"
                  :data="filteredCountries"
                  autocomplete
                  :allow-new="false"
                  :open-on-focus="true"
                  field="country_name"
                  icon="earth"
                  :disabled="initialStatus === 'deleted' && !clone"
                  :placeholder="$t('targeted_countries_autocomplete_tip')"
                  @input="updatePricing"
                  @typing="getFilteredCountries">
              </b-taginput>
            </div>
          </b-field>

          <b-field>
            <template v-slot:label>
              {{ $t('traffic_source_type_targeting') }}
              <b-tooltip type="is-dark" :label="$t('traffic_source_type_targeting_tooltip')" multilined>
                <b-icon size="is-small" icon="help-circle-outline"></b-icon>
              </b-tooltip>
            </template>
            <div>
              <b-field>
                <b-radio-button v-model="form.traffic_source_type" native-value="1"
                                :disabled="initialStatus === 'deleted' && !clone">
                  {{ $t('in_app') }}
                </b-radio-button>
                <b-radio-button v-model="form.traffic_source_type" native-value="2"
                                :disabled="initialStatus === 'deleted' && !clone">
                  {{ $t('mobile_web') }}
                </b-radio-button>
                <b-radio-button v-model="form.traffic_source_type" native-value="0"
                                :disabled="initialStatus === 'deleted' && !clone">
                  {{ $t('both') }}
                </b-radio-button>
              </b-field>
            </div>
          </b-field>

          <b-field>
            <template v-slot:label>
              {{ $t('narrow_down_targeting') }}
              <b-tooltip type="is-dark" :disabled="initialStatus === 'deleted' && !clone"
                         :label="locationTargetingHelp" multilined>
                <b-icon size="is-small" icon="help-circle-outline"></b-icon>
              </b-tooltip>
            </template>

            <div>
              <b-field>
                <b-radio-button v-model="location_type" native-value="region"
                                :disabled="initialStatus === 'deleted' && !clone">
                  {{ $t('state_region') }}
                </b-radio-button>
                <b-radio-button v-model="location_type" native-value="city"
                                :disabled="initialStatus === 'deleted' && !clone">
                  {{ $t('city') }}
                </b-radio-button>
              </b-field>
              <form autocomplete="off">
                <b-autocomplete
                    v-model="location_query"
                    :data="location_search_results"
                    placeholder="Start typing to search"
                    field="name"
                    :open-on-focus="open_on_focus"
                    :clear-on-select="clear_on_select"
                    :loading="is_fetching"
                    :disabled="initialStatus === 'deleted' && !clone"
                    @typing="searchLocation"
                    @select="addTargetedLocation">
                  <template slot-scope="props">{{ props.option.name }}</template>
                </b-autocomplete>
              </form>
              <div v-if="form.location_overrides && form.location_overrides.length > 0" class="top-spacer-20">
                <ul class="location-list">
                  <li v-for="(location, index) in form.location_overrides" :key="index">
                    {{ location.name }} <span class="delete is-small" @click="deleteLocationOverride(index)"></span>
                  </li>
                </ul>
              </div>
            </div>
          </b-field>

          <b-field v-if="form.attribution_provider === 'AppsFlyer'">
            <template v-slot:label>
              {{ $t('retargeting') }}
              <b-tooltip type="is-dark" :label="$t('retargeting_tooltip')" multilined>
                <b-icon size="is-small" icon="help-circle-outline"></b-icon>
              </b-tooltip>
            </template>
            <div class="control">
              <b-checkbox v-model="form.retargeting" :value="true"
                          :disabled="initialStatus === 'deleted' && !clone"></b-checkbox>
            </div>
          </b-field>
          <div class="redirect-device-row"
               :class="{ 'has-redirect': partnerLevel === 'gold' && (form.attribution_provider === 'AppsFlyer' || form.attribution_provider === 'Adjust') }">
            <b-field v-if="partnerLevel === 'gold' && (form.attribution_provider === 'AppsFlyer' || form.attribution_provider === 'Adjust')"
                     class="redirect-field">
              <template v-slot:label>
                {{ $t('client_side_redirects') }} *
                <b-tooltip type="is-dark" :label="$t('client_side_redirects_tooltip')" multilined>
                  <b-icon size="is-small" icon="help-circle-outline"></b-icon>
                </b-tooltip>
              </template>
              <div class="control">
                <span class="select">
                  <select v-model="form.client_side_redirects" required>
                    <option value=1>Client-to-Server (C2S) - Default</option>
                    <option value=0>Server-to-Server (S2S)</option>
                  </select>
                </span>
              </div>
            </b-field>
            <b-field v-if="form.platform_id !== 3" class="device-ifa-field">
              <template v-slot:label>
                {{ $t('device_ifa_required') }}
                <b-tooltip type="is-dark" :label="$t('device_ifa_required_tooltip')" multilined>
                  <b-icon size="is-small" icon="help-circle-outline"></b-icon>
                </b-tooltip>
              </template>
              <div class="control">
                <b-checkbox v-model="form.device_ifa_required" :value="true"
                            :disabled="initialStatus === 'deleted' && !clone"></b-checkbox>
              </div>
            </b-field>
          </div>

          <button type="button" class="disclosure-row" @click="advancedTargetingOpen = !advancedTargetingOpen">
            <span>{{ $t('advanced_targeting') }}</span>
            <span class="card-chevron" :class="{ 'is-open': advancedTargetingOpen }"></span>
          </button>
          <div class="disclosure-body" :class="{ 'is-open': advancedTargetingOpen }"><div class="disclosure-body-inner">

          <div v-if="partnerLevel === 'gold' || partnerLevel === 'silver'" class="os-version">
            <label class="label">{{ $t('operating_system_version') }}</label>
            <div class="os-version-bounds">
              <label class="os-bound">
                <span>{{ $t('minimum') }}</span>
                <b-input
                    v-model="form.min_os_version"
                    placeholder="e.g. 14.0"
                    :disabled="initialStatus === 'deleted' && !clone"
                    type="text"
                ></b-input>
              </label>
              <label class="os-bound">
                <span>{{ $t('maximum') }}</span>
                <b-input
                    v-model="form.max_os_version"
                    placeholder="e.g. 14.1"
                    :disabled="initialStatus === 'deleted' && !clone"
                    type="text"
                ></b-input>
              </label>
            </div>
          </div>

          <div class="allowed-filters">
            <div class="filter-field">
              <label class="label">{{ $t('carriers') }}</label>
              <b-input v-model="form.target_carriers" placeholder="e.g. 408,vzw,verizon"
                       :disabled="initialStatus === 'deleted' && !clone" type="text"></b-input>
            </div>

            <div class="filter-field">
              <label class="label">
                {{ $t('target_device_manufacturer') }}
                <b-tooltip type="is-dark" :label="$t('target_device_manufacturer_tooltip')" multilined>
                  <b-icon size="is-small" icon="help-circle-outline"></b-icon>
                </b-tooltip>
              </label>
              <b-input id="target_device_manufacturer_field"
                       v-model="form.target_device_manufacturer"
                       placeholder="e.g. Samsung"
                       :disabled="initialStatus === 'deleted' && !clone"
                       type="text"></b-input>
            </div>

            <div class="filter-field">
              <label class="label">{{ $t('exchanges') }}</label>
              <!-- Multi-select UI; form.target_exchanges stays a comma-separated string for Everflow encoding -->
              <div class="filter-picker"
                   :class="{ open: exchangePickerOpen, disabled: initialStatus === 'deleted' && !clone }"
                   @click.stop>
                <button type="button"
                        class="filter-picker-toggle"
                        :disabled="initialStatus === 'deleted' && !clone"
                        :aria-expanded="exchangePickerOpen ? 'true' : 'false'"
                        @click.prevent="toggleExchangePicker">
                  <span class="summary">{{ exchangeSummary }}</span>
                  <span class="chevron">▼</span>
                </button>
                <div class="filter-picker-panel" v-show="exchangePickerOpen">
                  <label v-for="e in exchangeOptions"
                         :key="e.id"
                         class="filter-option">
                    <input type="checkbox"
                           :value="String(e.id)"
                           v-model="selectedExchangeIds"
                           :disabled="initialStatus === 'deleted' && !clone"
                           @change="syncAllowedExchangesString">
                    {{ e.id }}: {{ e.name }}
                  </label>
                  <div class="filter-picker-actions">
                    <button type="button"
                            class="filter-clear"
                            :disabled="initialStatus === 'deleted' && !clone"
                            @click.prevent="clearExchanges">
                      Clear (All)
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div class="filter-field">
              <label class="label">{{ $t('impression_types') }}</label>
              <!-- Multi-select UI; form.target_impression_types stays a comma-separated string for Everflow encoding -->
              <div class="filter-picker"
                   :class="{ open: impressionTypePickerOpen, disabled: initialStatus === 'deleted' && !clone }"
                   @click.stop>
                <button type="button"
                        class="filter-picker-toggle"
                        :disabled="initialStatus === 'deleted' && !clone"
                        :aria-expanded="impressionTypePickerOpen ? 'true' : 'false'"
                        @click.prevent="toggleImpressionTypePicker">
                  <span class="summary">{{ impressionTypeSummary }}</span>
                  <span class="chevron">▼</span>
                </button>
                <div class="filter-picker-panel" v-show="impressionTypePickerOpen">
                  <label v-for="t in impressionTypeOptions"
                         :key="t.id"
                         class="filter-option">
                    <input type="checkbox"
                           :value="String(t.id)"
                           v-model="selectedImpressionTypeIds"
                           :disabled="initialStatus === 'deleted' && !clone"
                           @change="syncAllowedImpressionTypesString">
                    {{ t.id }}: {{ t.name }}
                  </label>
                  <div class="filter-picker-actions">
                    <button type="button"
                            class="filter-clear"
                            :disabled="initialStatus === 'deleted' && !clone"
                            @click.prevent="clearImpressionTypes">
                      Clear (All)
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div v-show="showManualAppCategories" class="filter-field">
              <label class="label">
                {{ $t('app_categories') }}
                <b-tooltip type="is-dark" :label="$t('app_categories_tooltip')" multilined>
                  <b-icon size="is-small" icon="help-circle-outline"></b-icon>
                </b-tooltip>
              </label>
              <div class="filter-picker"
                   :class="{ open: appCategoryPickerOpen, disabled: initialStatus === 'deleted' && !clone }"
                   @click.stop>
                <button type="button"
                        class="filter-picker-toggle"
                        :disabled="initialStatus === 'deleted' && !clone"
                        :aria-expanded="appCategoryPickerOpen ? 'true' : 'false'"
                        @click.prevent="toggleAppCategoryPicker">
                  <span class="summary">{{ appCategorySummary }}</span>
                  <span class="chevron">▼</span>
                </button>
                <div class="filter-picker-panel" v-show="appCategoryPickerOpen">
                  <label v-for="c in platformAppCategories"
                         :key="c.id"
                         class="filter-option">
                    <input type="checkbox"
                           :value="String(c.id)"
                           v-model="selectedAppCategoryIds"
                           :disabled="initialStatus === 'deleted' && !clone"
                           @change="syncManualAppCategoriesString">
                    {{ c.name }}
                  </label>
                  <div class="filter-picker-actions">
                    <button type="button"
                            class="filter-clear"
                            :disabled="initialStatus === 'deleted' && !clone"
                            @click.prevent="clearAppCategories">
                      Clear (All)
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div></div>
          </div>
            </div></div>
          </section>
          </div>

          <section class="ds-card" :class="{ 'is-collapsed': !openBudget }">
            <button type="button" class="card-head" @click="openBudget = !openBudget">
              <span class="card-head-text">
                <span class="ds-card-title">{{ $t('budget_and_caps') }}</span>
                <span v-if="!openBudget" class="card-summary">{{ budgetSummary }}</span>
              </span>
              <span class="card-chevron" :class="{ 'is-open': openBudget }"></span>
            </button>
            <div class="card-body" :class="{ 'is-open': openBudget }"><div class="card-body-inner">

          <div class="budget-strip" v-if="form.offer_id && !clone">
            <span><b>{{ $t('spend') }}</b> {{ spentBudget | toCurrency }}</span>
            <span class="budget-dot" aria-hidden="true">·</span>
            <span v-if="form.total_budget === undefined || form.total_budget === null || form.total_budget === ''">
              <b>{{ $t('budget') }}</b> {{ $t('unlimited') }}
            </span>
            <span v-else><b>{{ $t('budget') }}</b> {{ form.total_budget | toCurrency }}</span>
            <span class="budget-dot" aria-hidden="true">·</span>
            <span v-if="form.total_budget === undefined || form.total_budget === null || form.total_budget === ''">
              <b>{{ $t('remaining_budget') }}</b> {{ $t('unlimited') }}
            </span>
            <span v-else-if="spentBudget >= form.total_budget">
              <b>{{ $t('remaining_budget') }}</b> <span class="red">{{ 0 | toCurrency }}</span>
            </span>
            <span v-else-if="spentBudget >= 0.9 * form.total_budget">
              <b>{{ $t('remaining_budget') }}</b> <span class="yellow">{{ form.total_budget - spentBudget | toCurrency }}</span>
            </span>
            <span v-else>
              <b>{{ $t('remaining_budget') }}</b> {{ form.total_budget - spentBudget | toCurrency }}
            </span>
          </div>

          <div class="budget-fields">
            <b-field>
              <template v-slot:label>
                {{ $t('budget') }}
                <b-tooltip type="is-dark" :label="$t('total_budget_tooltip')" multilined>
                  <b-icon size="is-small" icon="help-circle-outline"></b-icon>
                </b-tooltip>
              </template>
              <b-input id="total_budget_field" icon="currency-usd" v-model="form.total_budget"
                       :disabled="initialStatus === 'deleted' && !clone" :placeholder="$t('budget')" type="number"
                       min="0.01" max="9999999.99" step="0.01"></b-input>
            </b-field>

            <b-field>
              <template v-slot:label>
                {{ $t('budget_type') }}
                <b-tooltip type="is-dark" :label="$t('budget_type_tooltip')" multilined>
                  <b-icon size="is-small" icon="help-circle-outline"></b-icon>
                </b-tooltip>
              </template>

              <div>
                <b-field>
                  <b-radio-button v-model="form.budget_type" native-value="1"
                                  :disabled="initialStatus === 'deleted' && !clone">
                    {{ $t('ltd_budget_type') }}
                  </b-radio-button>
                  <b-radio-button v-model="form.budget_type" native-value="2"
                                  :disabled="initialStatus === 'deleted' && !clone">
                    {{ $t('mtd_budget_type') }}
                  </b-radio-button>
                </b-field>
              </div>
            </b-field>
          </div>

          <div class="cap-fields">
          <b-field>
            <template v-slot:label>
              {{ $t('max_daily_spend') }}
              <b-tooltip type="is-dark" :label="$t('max_daily_spend_tooltip')" multilined>
                <b-icon size="is-small" icon="help-circle-outline"></b-icon>
              </b-tooltip>
            </template>
            <b-input
                id="max_daily_spend_field"
                icon="currency-usd"
                v-model="form.max_daily_spend"
                :disabled="initialStatus === 'deleted' && !clone"
                :placeholder="$t('max_daily_spend')"
                type="number"
                min="0.01"
                max="1000000"
                step="0.01"
               >
            </b-input>
          </b-field>

          <div class="field" :key="'daily_cap_' + form.billing_type_id">
            <label class="label">
              <template v-if="isCpiBilling">
                {{ $t('daily_install_cap') }} *
                <b-tooltip type="is-dark" :label="$t('daily_install_cap_tooltip')" multilined>
                  <b-icon size="is-small" icon="help-circle-outline"></b-icon>
                </b-tooltip>
              </template>
              <template v-else>
                {{ $t('daily_acquisition_cap') }} *
                <b-tooltip type="is-dark" :label="$t('daily_acquisition_cap_tooltip')" multilined>
                  <b-icon size="is-small" icon="help-circle-outline"></b-icon>
                </b-tooltip>
              </template>
            </label>
            <div class="control">
              <b-input :id="isCpiBilling ? 'daily_install_cap_field' : 'daily_acquisition_cap_field'"
                       v-model="form.daily_install_cap"
                       :placeholder="isCpiBilling ? $t('daily_install_cap') : $t('daily_acquisition_cap')"
                       :disabled="initialStatus === 'deleted' && !clone"
                       type="number" min="10" max="25000" required></b-input>
            </div>
          </div>
          <!-- Use explicit label markup (not only v-slot:label) so the label always renders for gold partners -->
          <div v-if="partnerLevel === 'gold'" class="field">
            <label class="label">
              {{ $t('daily_impression_cap') }}
              <b-tooltip type="is-dark" :label="$t('daily_impression_cap_tooltip')" multilined>
                <b-icon size="is-small" icon="help-circle-outline"></b-icon>
              </b-tooltip>
            </label>
            <div class="control">
              <b-input id="daily_impression_cap_field" v-model="form.daily_impression_cap"
                       :placeholder="$t('daily_impression_cap')"
                       :disabled="initialStatus === 'deleted' && !clone"
                       type="number" min="10000" max="2000000000"></b-input>
            </div>
          </div>

          </div>
          <b-field v-if="form.billing_type_id === BillingTypeEnum.CPI.id" style="margin-bottom: 0">
            <template v-slot:label>
              {{ $t('kpis') }}
              <b-tooltip type="is-dark" :label="$t('kpis_tooltip')" multilined>
                <b-icon size="is-small" icon="help-circle-outline"></b-icon>
              </b-tooltip>
            </template>
            <b-input v-model="form.hard_kpis" type="textarea" maxlength="1000"
                     :disabled="initialStatus === 'deleted' && !clone" class="wrap-text"></b-input>
          </b-field>

          <b-field v-if="form.offer_id && clone">
            <b-checkbox v-model="form.clone_whitelist" :disabled="initialStatus === 'deleted' && !clone">
              {{ $t('clone_whitelist') }}
            </b-checkbox>
          </b-field>
          <b-field v-if="form.offer_id && clone">
            <b-checkbox v-model="form.clone_blacklist" :disabled="initialStatus === 'deleted' && !clone">
              {{ $t('clone_blacklist') }}
            </b-checkbox>
          </b-field>
          <b-field v-if="form.offer_id && clone && form.creatives.length > 0">
            <b-checkbox v-model="form.clone_creatives" :disabled="initialStatus === 'deleted' && !clone">
              {{ $t('clone_creatives') }}
            </b-checkbox>
          </b-field>
            </div></div>
          </section>

          <section id="tracking" class="ds-card tracking-card" :class="{ 'is-collapsed': !openTracking }">
            <button type="button" class="card-head" @click="openTracking = !openTracking">
              <span class="card-head-text">
                <span class="ds-card-title">{{ $t('tracking_attribution') }}</span>
                <span v-if="!openTracking" class="card-summary">{{ trackingSummary }}</span>
              </span>
              <span class="card-chevron" :class="{ 'is-open': openTracking }"></span>
            </button>
            <div class="card-body" :class="{ 'is-open': openTracking }"><div class="card-body-inner">
              <Attribution :offer="form" :disabled="initialStatus === 'deleted' && !clone" :isSuperUser="isSuperUser"></Attribution>
            </div></div>
          </section>

          <section id="optimize" class="ds-card" :class="{ 'is-collapsed': !openOptimize }">
            <button type="button" class="card-head" @click="openOptimize = !openOptimize">
              <span class="card-head-text">
                <span class="ds-card-title">{{ $t('optimize') }}</span>
                <span v-if="!openOptimize" class="card-summary">{{ optimizeSummary }}</span>
              </span>
              <span class="card-chevron" :class="{ 'is-open': openOptimize }"></span>
            </button>
            <div class="card-body" :class="{ 'is-open': openOptimize }"><div class="card-body-inner">
              <div class="optimize-links" v-if="form.offer_id && !clone">
                <a target="_blank" :href="'clone_campaign.html?offer_id=' + form.offer_id">{{ $t('clone_campaign') }}</a>
                <a target="_blank" :href="'sources.html?offer_id=' + form.offer_id">{{ $t('optimize_sources') }}</a>
                <a target="_blank" :href="'subsites.html?offer_id=' + form.offer_id">{{ $t('optimize_subsites') }}</a>
                <a target="_blank" :href="'pie_optimization.html?offer_id=' + form.offer_id">{{ $t('optimize_events') }}</a>
                <a target="_blank" :href="'suppression_lists.html?offer_id=' + form.offer_id">{{ $t('optimize_suppression') }}</a>
              </div>
              <PIESetupForm :campaign="form" :clone="clone" :disabled="initialStatus === 'deleted' && !clone" :isSuperUser="isSuperUser"/>
            </div></div>
          </section>

          <section v-if="isSuperUser" class="ds-card" :class="{ 'is-collapsed': !openAdmin }">
            <button type="button" class="card-head" @click="openAdmin = !openAdmin">
              <span class="card-head-text">
                <span class="ds-card-title">{{ $t('admin_settings') }}</span>
                <span v-if="!openAdmin" class="card-summary">{{ adminSummary }}</span>
              </span>
              <span class="card-chevron" :class="{ 'is-open': openAdmin }"></span>
            </button>
            <div class="card-body" :class="{ 'is-open': openAdmin }"><div class="card-body-inner">
              <div class="provider-row">
                <b-field>
                  <template v-slot:label>
                    {{ $t('provider') }} *
                  </template>
                  <div class="control">
                    <span class="select">
                      <select v-model="form.provider_id" required
                              :disabled="initialStatus === 'deleted' && !clone">
                        <option v-for="provider in providers"
                                :value="provider.provider_id"
                                :key="provider.provider_id"
                                :selected="provider.provider_id === form.provider_id">
                          {{ provider.provider_id }}: {{ provider.name }}
                        </option>
                      </select>
                    </span>
                  </div>
                </b-field>
                <b-field class="pushed-field">
                  <b-switch v-model="form.pushed" :key="form.offer_id" :native-value="form.offer_id" :value="true"
                            type="is-success" v-on:input="flipPushStatus" :disabled="initialStatus === 'deleted' && !clone">
                    <b>{{ $t('pushed') }}</b>
                  </b-switch>
                </b-field>
              </div>
              <div class="superuser-arbitrage-fields">
                <b-field>
                  <template v-slot:label>
                    {{ $t('cpa_cpi_arbitrage') }}
                    <b-tooltip type="is-dark" :label="$t('cpa_cpi_arbitrage_tooltip')" multilined>
                      <b-icon size="is-small" icon="help-circle-outline"></b-icon>
                    </b-tooltip>
                  </template>
                  <div class="control">
                    <b-checkbox v-model="form.cpa_cpi_arbitrage" :value="true"
                                :disabled="initialStatus === 'deleted' && !clone"></b-checkbox>
                  </div>
                </b-field>
                <b-field>
                  <template v-slot:label>
                    {{ $t('cpa_cpm_arbitrage') }}
                    <b-tooltip type="is-dark" :label="$t('cpa_cpm_arbitrage_tooltip')" multilined>
                      <b-icon size="is-small" icon="help-circle-outline"></b-icon>
                    </b-tooltip>
                  </template>
                  <div class="control">
                    <b-checkbox v-model="form.cpa_cpm_arbitrage" :value="true"
                                :disabled="initialStatus === 'deleted' && !clone"
                                v-on:input="onCpaCpmArbitrageChange"></b-checkbox>
                  </div>
                </b-field>
                <b-field>
                  <template v-slot:label>
                    {{ $t('use_dcpi') }}
                    <b-tooltip type="is-dark" :label="$t('use_dcpi_tooltip')" multilined>
                      <b-icon size="is-small" icon="help-circle-outline"></b-icon>
                    </b-tooltip>
                  </template>
                  <div class="control">
                    <b-checkbox v-model="form.use_dcpi" :value="false"
                                :disabled="(initialStatus === 'deleted' && !clone) || !form.cpa_cpm_arbitrage"></b-checkbox>
                  </div>
                </b-field>
                <b-field>
                  <template v-slot:label>
                    {{ $t('full_trust_mature_installs') }}
                    <b-tooltip type="is-dark" :label="$t('full_trust_mature_installs_tooltip')" multilined>
                      <b-icon size="is-small" icon="help-circle-outline"></b-icon>
                    </b-tooltip>
                  </template>
                  <b-input
                      id="full_trust_mature_installs_field"
                      v-model="form.full_trust_mature_installs"
                      :disabled="(initialStatus === 'deleted' && !clone) || !form.cpa_cpm_arbitrage"
                      :placeholder="$t('full_trust_mature_installs')"
                      type="number"
                      min="1"
                      max="100000"
                      step="1"
                     >
                  </b-input>
                </b-field>
                <b-field>
                  <template v-slot:label>
                    {{ $t('arpu_cohort_days') }}
                    <b-tooltip type="is-dark" :label="$t('arpu_cohort_days_tooltip')" multilined>
                      <b-icon size="is-small" icon="help-circle-outline"></b-icon>
                    </b-tooltip>
                  </template>
                  <b-input
                      id="arpu_cohort_days_field"
                      v-model="form.arpu_cohort_days"
                      :disabled="(initialStatus === 'deleted' && !clone) || !form.cpa_cpm_arbitrage"
                      :placeholder="$t('arpu_cohort_days')"
                      type="number"
                      min="1"
                      max="90"
                      step="1"
                     >
                  </b-input>
                </b-field>
                <b-field>
                  <template v-slot:label>
                    {{ $t('target_cohort_roas') }}
                    <b-tooltip type="is-dark" :label="$t('target_cohort_roas_tooltip')" multilined>
                      <b-icon size="is-small" icon="help-circle-outline"></b-icon>
                    </b-tooltip>
                  </template>
                  <b-input
                      id="target_cohort_roas_field"
                      v-model="form.target_cohort_roas"
                      :disabled="(initialStatus === 'deleted' && !clone) || !form.cpa_cpm_arbitrage"
                      :placeholder="$t('target_cohort_roas')"
                      type="number"
                      min="0.001"
                      max="999.999"
                      step="0.001"
                     >
                  </b-input>
                </b-field>
              </div>
            </div></div>
          </section>

          <section class="ds-card" v-if="form.offer_id && (!clone || form.clone_creatives)" :class="{ 'is-collapsed': !openCreatives }">
            <button type="button" class="card-head" @click="openCreatives = !openCreatives">
              <span class="card-head-text">
                <span class="ds-card-title">{{ $t('creatives') }}</span>
                <span v-if="!openCreatives" class="card-summary">{{ creativesSummary }}</span>
              </span>
              <span class="card-chevron" :class="{ 'is-open': openCreatives }"></span>
            </button>
            <div class="card-body" :class="{ 'is-open': openCreatives }"><div class="card-body-inner">

            <div v-if="!clone && form.top_banner_sizes_check === false && form.platform_id !== 3" class="sizes-banner">
              <span>
                {{ $t('add_top_creative_sizes') }}
                <b>
                  <span v-for="(size, index) in form.missing_top_banner_sizes" :key="index">
                    <span>{{ size }}</span>
                    <span v-if="index < form.missing_top_banner_sizes.length - 1">, </span>
                  </span>
                </b>
              </span>
              <button type="button" class="ds-button button-small w-button" v-on:click="addCreatives"
                      :disabled="initialStatus === 'deleted' && !clone">
                {{ $t('add_now') }}
              </button>
            </div>
            <div class="top-spacer-10" v-if="!clone && form.video_creative_check === false && form.platform_id === 3">
              {{ $t('add_video_creatives') }}
            </div>
            <div class="top-spacer-10" v-else-if="!clone && form.video_creative_check === false">
              {{ $t('add_video_and_companion_creatives') }}
            </div>

            <div class="creative-help-text bottom-spacer-10">
              <p>
                {{ $t('creative_report_preview_help_before') }}
                <a :href="creativeReportUrl">{{ $t('report') }}</a>{{ $t('creative_report_preview_help_after') }}
              </p>
            </div>

          <b-field v-if="form.offer_id && (!clone || form.clone_creatives) && form.creatives.length > 0"
                   :label="$t('creatives')" class="creatives-container" style="margin-bottom: 0">

            <div class="top-spacer-10 inline">
              <b-field>
                <b-radio-button v-model="selected_creative_status"
                                native-value="active"
                                v-on:input="filterCreatives">
                  {{ $t('active') }}
                </b-radio-button>
                <b-radio-button v-model="selected_creative_status"
                                native-value="inactive"
                                v-on:input="filterCreatives">
                  {{ $t('inactive') }}
                </b-radio-button>
                <b-radio-button v-model="selected_creative_status"
                                native-value="all"
                                v-on:input="filterCreatives">
                  {{ $t('all') }}
                </b-radio-button>
              </b-field>

              <div class="left-spacer-30">
                <b-button v-if="checkboxes.creativeStatuses.length > 0" @click="inactivateAllCreatives()">
                  {{ $t('inactivate_all_creatives') }}
                </b-button>
              </div>

              <div class="left-spacer-30">
                <b-button @click="showManageSchedulesPopup()">{{ $t('manage_schedules') }}</b-button>
              </div>
            </div>
          </b-field>

          <b-tabs type="is-boxed"
                  v-if="form.offer_id && (!clone || form.clone_creatives) && filtered_creatives.length > 0">
            <b-tab-item v-for="group in used_schedule_groups" :key="group.id" :label="group.group_name">
              <div class="creative-grid">
                <div v-for="file in creativesForGroup(group)" :key="file.creative_id"
                     class="creative-container">
                  <CreativeDisplay :platformId="form.platform_id" :file="file" :checkboxes="checkboxes"
                                   :creatives="form.creatives" :languages="languages"
                                   :scheduleGroups="schedule_groups_with_blank" :creativeTypes="creativeTypes"
                                   :filterCreatives="filterCreatives"/>
                </div>
              </div>
              <button v-if="filterByGroupId(filtered_creatives, group.id).length > 10"
                      type="button" class="creative-more" @click="toggleCreativeGroup(group.id)">
                <b-icon :icon="expandedCreativeGroups[group.id] ? 'menu-up' : 'menu-down'"></b-icon>
                {{ expandedCreativeGroups[group.id] ? 'Hide' : 'Show more creatives' }}
              </button>
            </b-tab-item>
          </b-tabs>

          <div class="button-container" v-if="form.offer_id && !clone">
            <button class="ds-button button-small w-button" v-on:click="addCreatives"
                    :disabled="initialStatus === 'deleted' && !clone" type="button">
              {{ $t('add_creatives') }}
            </button>
            <button class="ds-button button-small w-button left-spacer-30" v-on:click="showManageSchedulesPopup"
                    :disabled="initialStatus === 'deleted' && !clone" type="button">
              {{ $t('manage_schedules') }}
            </button>
            <button v-if="hasPendingCreatives" class="ds-button button-small w-button left-spacer-10"
                    v-on:click="reviewCreatives" type="button">
              {{ $t('review_creatives') }}
            </button>
          </div>
            </div></div>
          </section>

        </form>
    </div>
    <div class="campaign-rail" v-if="form.offer_id && !clone">
          <div class="kpi-card">
            <div class="kpi-label kpi-label-with-help">
              {{ $t('impressions') }}
              <span class="kpi-help"
                    tabindex="0"
                    @mouseenter="showUtcTip"
                    @focus="showUtcTip"
                    @mouseleave="hideUtcTip"
                    @blur="hideUtcTip">
                <b-icon size="is-small" icon="help-circle-outline"></b-icon>
              </span>
            </div>
            <div class="kpi-value">{{ form.impressions_today | toNumber }}</div>
            <div class="kpi-sub">{{ $t('ltd') }} {{ form.impressions_ltd | toNumber }}</div>
            <div v-if="ecpiLtd !== null" class="kpi-extra">
              <span class="kpi-label">{{ $t('ecpi_ltd') }}</span>
              <span class="kpi-extra-value">{{ ecpiLtd | toCurrency }}</span>
            </div>
          </div>
          <div class="kpi-card" v-if="form.platform_id !== 3 || cvrLtd !== null">
            <template v-if="form.platform_id !== 3">
              <div class="kpi-label">{{ $t('clicks') }}</div>
              <div class="kpi-value">{{ form.clicks_today | toNumber }}</div>
              <div class="kpi-sub">{{ $t('ltd') }} {{ form.clicks_ltd | toNumber }}</div>
              <div v-if="cvrLtd !== null" class="kpi-extra">
                <span class="kpi-label">{{ $t('cvr_ltd') }}</span>
                <span class="kpi-extra-value">{{ cvrLtd | toPercent }}</span>
              </div>
            </template>
            <template v-else-if="cvrLtd !== null">
              <div class="kpi-label">{{ $t('cvr_ltd') }}</div>
              <div class="kpi-value">{{ cvrLtd | toPercent }}</div>
              <div class="kpi-sub">{{ $t('ltd') }}</div>
            </template>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">{{ $t('conversions') }}</div>
            <div class="kpi-value">{{ form.installs_today | toNumber }}</div>
            <div class="kpi-sub">{{ $t('ltd') }} {{ form.installs_ltd | toNumber }}</div>
          </div>
          <section class="ds-card status-card">
            <h2 class="ds-card-title">{{ $t('health') }}</h2>
            <CampaignChecks :campaign="form" :isSuperUser="isSuperUser" :partnerLevel="partnerLevel"></CampaignChecks>
          </section>
          <section class="ds-card sift-id-card rail-id">
            <h2 class="ds-card-title">{{ $t('sift_campaign_id') }}</h2>
            <div>{{ form.campaign_id }}</div>
          </section>
    </div>
    </div>

    <div class="sticky-save">
      <button class="ds-button button-small w-button" v-on:click="submit"
              :disabled="initialStatus === 'deleted' && !clone" type="button">
        {{ $t('save_campaign') }}
      </button>
      <a href="dashboard.html" class="sticky-return">{{ $t('return_to_campaign_list') }}</a>
      <span class="green sticky-saved" v-if="showSaveMessage && !errors.length">{{ $t('campaign_was_saved_successfully') }}</span>
      <div class="sticky-errors" v-if="errors.length">
        <div v-for="error in errors" :key="error">{{ $t(error) }}</div>
      </div>
    </div>
    <section>
      <b-modal :active.sync="is_schedules_popup_visible" has-modal-card>
        <div class="modal-card" style="min-width: 900px">
          <header class="modal-card-head">
            <p class="modal-card-title">{{ $t('creative_schedules') }}</p>
          </header>
          <section class="modal-card-body">

            <div class="bottom-spacer-20">{{ $t('creative_schedules_help_text') }}</div>

            <b-collapse v-for="group in schedule_groups" :key="group.id" class="card" animation="slide"
                        :open="group.id === null">
              <template #trigger="props">
                <div class="card-header" role="button">
                  <p class="card-header-title">
                    {{ group.group_name }}
                  </p>
                  <a class="card-header-icon">
                    <b-icon :icon="props.open ? 'menu-down' : 'menu-up'"></b-icon>
                  </a>
                </div>
              </template>
              <div class="card-content group">
                <div class="content">
                  <b-field>
                    <template v-slot:label>
                      {{ $t('schedule_name') }} *
                    </template>
                    <b-input :disabled="initialStatus === 'deleted' && !clone" v-model="group.group_name"
                             required></b-input>
                  </b-field>

                  <div v-for="schedule in group.schedules" :key="schedule.id" class="schedules-container">
                    <div class="schedule">
                      <div>
                        <b-field>
                          <template v-slot:label>
                            {{ $t('date_range') }} *
                          </template>
                          <b-datepicker v-model="schedule.dates" :date-formatter="formatDateRange" range required
                                        class="date-range-picker"
                                        :disabled="initialStatus === 'deleted' && !clone"></b-datepicker>
                        </b-field>
                      </div>

                      <div class="top-spacer-20">
                        <b-field>
                          <template v-slot:label>
                            {{ $t('days_of_the_week') }} *
                          </template>
                          <div>
                            <b-checkbox v-for="dayOfWeek in daysOfWeek" v-model="schedule.days_of_the_week"
                                        :key="dayOfWeek.id" :native-value="dayOfWeek.id"
                                        :disabled="initialStatus === 'deleted' && !clone"> {{ dayOfWeek.name }}
                            </b-checkbox>
                          </div>
                        </b-field>
                      </div>
                    </div>
                  </div>

                  <div v-if="scheduleCompleted(group)" class="top-spacer-20">
                    <b-button @click="addSchedule(group)">{{ $t('add_another_date_range') }}</b-button>
                  </div>
                </div>
              </div>
            </b-collapse>

            <ErrorMessages :errors="schedule_groups_errors"></ErrorMessages>

            <div v-if="scheduleGroupsCompleted" class="top-spacer-20">
              <b-button @click="addScheduleGroup()">{{ $t('add_another_schedule') }}</b-button>
            </div>

            <div id="manageSchedulesBottom"></div>
          </section>
          <footer class="modal-card-foot">
            <button class="button is-primary" type="button" @click="saveSchedules">Save</button>
            <button class="button" type="button" @click="hideSchedulesPopup">Cancel</button>
          </footer>
        </div>
      </b-modal>
    </section>
  </div>
</template>

<style>
.page-content.campaign-edit,
.page-content.campaign-edit.wide_page {
  max-width: none;
  width: auto;
  margin-left: 24px;
  margin-right: 24px;
  padding-bottom: 96px;
  background: #f4f6fb;
}

.campaign-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-top: 28px;
  margin-bottom: 16px;
}

.campaign-title {
  margin: 0;
  color: #0e0c1a;
  font-size: 1.7rem;
  line-height: 1.25;
  text-align: left;
}

.campaign-header-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-top: 8px;
  color: #3f3c55;
}

.meta-chip,
.status-pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  background: #e4e3ea;
  color: #0e0c1a;
  font-size: 0.85rem;
}

.status-active {
  background: #e7f6ef;
  color: #06724f;
}

.status-paused,
.status-pending {
  background: #fff3c4;
  color: #7a4e00;
}

.status-deleted {
  background: #fde8ee;
  color: #f94069;
}

.meta-app {
  word-break: break-all;
}

.campaign-header-links {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px 16px;
}

.campaign-header-links a {
  color: #353fff;
}

.campaign-layout.has-rail {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(240px, 300px);
  gap: 16px;
  align-items: start;
  margin-bottom: 16px;
}

.rail-id {
  margin-bottom: 0;
  padding: 10px 12px;
  min-width: 0;
}

.rail-id .ds-card-title {
  margin-bottom: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #3f3c55;
}

.rail-id > div {
  color: #0e0c1a;
  font-size: 1.15rem;
  font-weight: 600;
  line-height: 1.2;
}

.campaign-layout.has-rail > .campaign-main {
  grid-column: 1;
  grid-row: 1;
  min-width: 0;
  max-width: 100%;
}

.campaign-rail {
  grid-column: 2;
  grid-row: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  position: sticky;
  top: 16px;
  align-self: start;
  max-height: calc(100vh - 16px - 72px);
  overflow-y: auto;
  z-index: 2;
}

.campaign-rail > .ds-card {
  margin-bottom: 0;
  padding: 12px 12px 8px;
}

.campaign-rail .ds-card-title {
  font-size: 1rem;
  margin-bottom: 8px;
}

.campaign-rail .health-summary-text {
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.campaign-rail .health-verdict {
  font-size: 1rem;
}

.campaign-rail .health-note {
  font-size: 0.8rem;
  line-height: 1.35;
}

.campaign-rail .optimize-links a {
  font-size: 0.9rem;
}

.kpi-card {
  min-width: 0;
  height: auto;
  background: #fff;
  border: 1px solid #e6e8ef;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  padding: 10px 12px;
}

.kpi-extra {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 6px;
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid #f1f1f3;
}

.kpi-extra-value {
  color: #0e0c1a;
  font-weight: 600;
}

.stacked-label .label {
  display: block;
  white-space: normal;
}

.field-title {
  display: inline;
}

.field-hint {
  display: block;
  margin-top: 2px;
  color: #3f3c55;
  font-size: 0.8rem;
  font-weight: 400;
  line-height: 1.3;
}

.os-version {
  margin-bottom: 1.25rem;
}

.os-version > .label {
  margin-bottom: 6px;
}

.os-version-bounds {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.os-bound {
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: #363636;
  font-size: 0.85rem;
  font-weight: 600;
}

.os-bound .control,
.os-bound .input {
  width: 100%;
}

.kpi-label {
  color: #3f3c55;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.kpi-label-with-help {
  display: flex;
  align-items: center;
  gap: 4px;
}

.kpi-help {
  display: inline-flex;
  cursor: help;
  line-height: 1;
}

.utc-tip {
  position: fixed;
  z-index: 40;
  width: 240px;
  transform: translate(-50%, calc(-100% - 7px));
  padding: 0.35rem 0.75rem;
  border-radius: 6px;
  background: #363636;
  color: #fff;
  font-size: 0.85rem;
  font-weight: 400;
  line-height: 1.4;
  text-align: center;
  text-transform: none;
  letter-spacing: normal;
  box-shadow: 0 1px 2px 1px rgba(0, 1, 0, 0.2);
  pointer-events: none;
}

.utc-tip:after {
  content: "";
  position: absolute;
  left: 50%;
  bottom: -5px;
  transform: translateX(-50%);
  border-top: 5px solid #363636;
  border-right: 5px solid transparent;
  border-left: 5px solid transparent;
}

.kpi-value {
  color: #0e0c1a;
  font-size: 1.15rem;
  font-weight: 600;
  line-height: 1.2;
  margin-top: 2px;
}

.kpi-sub {
  color: #3f3c55;
  font-size: 0.8rem;
  margin-top: 2px;
}

.campaign-main {
  min-width: 0;
}

.budget-strip {
  display: flex;
  flex-wrap: nowrap;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
  max-width: 100%;
  margin: 0 0 14px;
  overflow: hidden;
  white-space: nowrap;
  color: #243044;
  font-size: 0.95rem;
}

.budget-strip b {
  margin-right: 4px;
  color: #3f3c55;
  font-weight: 600;
}

.budget-dot {
  color: #8b90a3;
}

.budget-fields {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 8px 24px;
  margin-bottom: 12px;
}

.budget-fields > .field {
  margin-left: 0;
  margin-bottom: 0.75rem;
}

.cap-fields {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 8px 28px;
}

.cap-fields > .field {
  margin-bottom: 0.5rem;
  min-width: 180px;
}

.card-pair {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 16px;
  align-items: start;
  margin-bottom: 16px;
  min-width: 0;
  max-width: 100%;
}

.card-pair > .ds-card {
  margin-bottom: 0;
  min-width: 0;
  max-width: 100%;
}

.card-pair .inline {
  flex-wrap: wrap;
}

.ds-card {
  background: #fff;
  border: 1px solid #e6e8ef;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  padding: 14px 16px 16px;
  margin-bottom: 16px;
}

.ds-card.is-collapsed {
  padding-bottom: 14px;
}

.ds-card .field:last-child {
  margin-bottom: 0;
}

.ds-card .label {
  font-size: 0.92rem;
  font-weight: 600;
  margin-bottom: 0.3rem;
}

.card-logo {
  width: 36px;
  height: 36px;
  object-fit: cover;
  border-radius: 6px;
  flex-shrink: 0;
}

.platform-app-row {
  display: flex;
  align-items: flex-end;
  gap: 12px 16px;
  flex-wrap: wrap;
  margin-bottom: 0.75rem;
}

.platform-app-row > .field {
  margin-bottom: 0;
}

.platform-app-row .app-id-field {
  flex: 1 1 220px;
  min-width: 180px;
}

.platform-app-row .app-textbox,
.platform-app-row .app-id-field .control,
.platform-app-row .app-id-field .input {
  width: 100%;
}

.platform-app-row .thumbnail {
  margin-bottom: 2px;
}

.billing-row > .field {
  margin-bottom: 0.75rem;
}

.billing-control {
  display: flex;
  align-items: center;
  gap: 8px 16px;
}

.billing-value {
  flex: 0 0 auto;
  font-weight: 600;
}

.billing-note {
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  color: #363636;
  font-size: 0.9rem;
  line-height: 1.35;
}


.allowed-filters {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.25rem 16px;
  align-items: start;
  margin: 0;
}

@media (max-width: 480px) {
  .campaign-edit .allowed-filters {
    grid-template-columns: minmax(0, 1fr);
  }
}

.redirect-device-row {
  display: grid;
  grid-template-columns: minmax(220px, 360px) minmax(180px, 280px);
  gap: 8px 28px;
  align-items: start;
  justify-content: start;
}

.redirect-device-row > .field {
  margin-bottom: 1.25rem;
}

.redirect-device-row.has-redirect .device-ifa-field {
  grid-column: 2;
}

.allowed-filters .control,
.allowed-filters .input,
.allowed-filters .select,
.allowed-filters .select select {
  width: 100%;
  max-width: none;
}

.ds-card .billing-control {
  width: 100%;
}

.ds-card .billing-control .select,
.ds-card .billing-control .select select {
  width: auto;
  max-width: none;
  flex: 0 0 auto;
}

.os-bound {
  flex: 1 1 0;
  min-width: 0;
}

.card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  margin: 0;
  padding: 0 0 12px;
  background: transparent;
  border: 0;
  border-bottom: 1px solid transparent;
  color: inherit;
  font-family: inherit;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.2s ease, padding 0.2s ease;
}

.ds-card:not(.is-collapsed) .card-head {
  border-bottom-color: #eef0f5;
  margin-bottom: 14px;
}

.ds-card.is-collapsed .card-head {
  margin-bottom: 0;
  padding-bottom: 0;
}

.card-body,
.disclosure-body {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.22s ease;
}

.card-body.is-open,
.disclosure-body.is-open {
  grid-template-rows: 1fr;
}

.disclosure-body {
  margin-bottom: 8px;
  overflow: hidden;
}

.disclosure-body:not(.is-open) {
  visibility: hidden;
}

.disclosure-body.is-open {
  visibility: visible;
  animation: disclosure-unclip 0.22s linear forwards;
}

@keyframes disclosure-unclip {
  0%, 99% { overflow: hidden; }
  100% { overflow: visible; }
}

.card-body-inner,
.disclosure-body-inner {
  min-height: 0;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
}

.tracking-card,
.tracking-card .card-body,
.tracking-card .card-body-inner {
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
}

.card-body.is-open > .card-body-inner,
.disclosure-body.is-open > .disclosure-body-inner {
  overflow: visible;
  visibility: visible;
}

.tracking-card .card-body.is-open > .card-body-inner {
  overflow: hidden;
}

.card-head-main {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.card-head-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.card-head .ds-card-title,
.sift-id-card .ds-card-title {
  margin: 0;
}

.card-summary {
  color: #3f3c55;
  font-size: 0.9rem;
  font-weight: 400;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-chevron {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  position: relative;
}

.card-chevron::before {
  content: "";
  position: absolute;
  top: 4px;
  left: 4px;
  width: 7px;
  height: 7px;
  border-right: 2px solid #5c6478;
  border-bottom: 2px solid #5c6478;
  transform: rotate(45deg);
  transition: transform 0.2s ease;
}

.card-chevron.is-open::before {
  top: 7px;
  transform: rotate(-135deg);
}

.ds-card-title {
  margin: 0 0 14px;
  color: #0e0c1a;
  font-size: 1.15rem;
  font-weight: 600;
}

.disclosure-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin: 2px 0 8px;
  padding: 8px 0;
  background: transparent;
  border: 0;
  border-top: 1px solid #eef0f5;
  color: #3f4658;
  font-family: inherit;
  font-size: 0.92rem;
  font-weight: 600;
  cursor: pointer;
  text-align: left;
}

.disclosure-body-inner {
  padding: 2px 0 8px;
}

.disclosure {
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin: 4px 0 12px;
  padding: 8px 10px;
  background: #fff;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  color: #0e0c1a;
  font-size: 1rem;
  cursor: pointer;
  text-align: left;
}

.imp-type-max-help {
  margin: 0 0 10px;
  color: #5c6478;
  font-size: 0.85rem;
}

.imp-type-max-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 8px 16px;
  margin-bottom: 8px;
}

.imp-type-max {
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: #3f4658;
  font-size: 0.85rem;
}

.cpi-hint {
  color: #cc1a1a;
  margin-bottom: 12px;
}

.sizes-banner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  padding: 10px 12px;
  background: #fffde4;
  border: 1px solid #ffbe40;
  border-radius: 6px;
  color: #363636;
}

.optimize-links {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.optimize-links a {
  color: #243056;
  background: #f4f6fb;
  border: 1px solid #e6e8ef;
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 0.88rem;
  font-weight: 600;
  text-decoration: none;
}

.optimize-links a:hover {
  background: #e8eefc;
  border-color: #c9d4f5;
}

.sticky-save {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 30;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px 20px;
  padding: 12px 24px;
  background: #fff;
  border-top: 1px solid #f1f1f3;
  box-shadow: 0 -2px 8px rgba(14, 12, 26, 0.06);
}

.sticky-return {
  color: #353fff;
}

.sticky-saved {
  font-size: 0.95rem;
}

.sticky-errors {
  flex: 1 1 240px;
  min-width: 0;
  max-height: 4.8em;
  overflow-y: auto;
  color: #ff0031;
  font-size: 0.95rem;
  line-height: 1.35;
}

.creative-grid-note {
  display: block;
}

@media (max-width: 1100px) {
  .campaign-layout.has-rail,
  .card-pair {
    display: block;
  }

  .campaign-rail {
    position: static;
    max-height: none;
    overflow: visible;
  }

  .campaign-rail > .kpi-card,
  .campaign-layout.has-rail > .rail-id,
  .campaign-rail > .ds-card,
  .card-pair > .ds-card {
    margin-bottom: 16px;
  }

  .campaign-header {
    flex-direction: column;
  }
}

.app-textbox {
  display: inline-block;
  width: 100%;
  padding-right: 10px;
}

.transparent-background {
  background: rgba(255, 255, 255, 0.0);
}

.table {
  width: 100%;
  border: none;
}

.label {
  font-size: 18px;
  color: #0e0c1a;
}

.campaign-edit .input,
.campaign-edit .textarea,
.campaign-edit .select select,
.campaign-edit .help,
.campaign-edit .checkbox,
.campaign-edit .radio {
  color: #0e0c1a;
}

.campaign-edit .input::placeholder,
.campaign-edit .textarea::placeholder {
  color: #5c5870;
  font-style: italic;
  opacity: 1;
}

.campaign-edit .os-bound .input::placeholder {
  color: #c4c1d4;
}

.top-negative-spacing-22px {
  margin-top: -22px
}

.stacked {
  display: block;
}



.columns {
  display: inline;
}

.column {
  float: left;
}

.inline {
  display: inline-flex;
}

.provider-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 8px 28px;
}

.campaign-edit .provider-row > .field {
  margin: 0 0 1.25rem;
}

.provider-row .pushed-field {
  padding-bottom: 0.4rem;
}

.superuser-arbitrage-fields {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  column-gap: 40px;
  row-gap: 0.25rem;
  width: 100%;
  max-width: 100%;
}

.left-side-column {
  min-width: 60%;
}

.right-side-column {
  min-width: 40%;
}

.creative-help-text {
  color: #363636;
  max-width: 52rem;
}

.creatives-container {
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  margin-bottom: 0;
}

.creative-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
  gap: 12px 14px;
  align-items: start;
  width: 100%;
  margin-bottom: 8px;
}

.creative-more {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin: 0 0 12px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #353fff;
  font: inherit;
  cursor: pointer;
}

.creative-container {
  display: block;
  min-width: 0;
  width: auto;
  margin: 0;
}

.dark-gray {
  color: #363636;
}

.red {
  color: #f40000;
}

.yellow {
  color: #ffbe40;
}

.yellow-background {
  background-color: #fffde4;
}

.green {
  color: #40a200;
}

.taginput .taginput-container > .tag, .taginput .taginput-container > .tags {
  font-size: 1em;
}

/* Allowed impression types: overlay multi-select (same idea as reports query filter pickers) */
.filter-picker {
  position: relative;
  z-index: 1;
  width: 100%;
  min-width: 0;
  max-width: none;
}
.filter-picker.open {
  z-index: 40;
}
.filter-picker.disabled {
  opacity: 0.6;
}
.filter-picker-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  box-sizing: border-box;
  min-height: 2.25em;
  padding: 0.4em 0.75em;
  background: #fff;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  font-size: 1em;
  cursor: pointer;
  text-align: left;
}
.filter-picker-toggle:disabled {
  cursor: not-allowed;
  background: #f5f5f5;
}
.filter-picker-toggle .summary {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.filter-picker-toggle .chevron {
  margin-left: 8px;
  color: #555;
  font-size: 12px;
}
.filter-picker.open .filter-picker-toggle .chevron {
  transform: rotate(180deg);
}
.filter-picker-panel {
  position: absolute;
  left: 0;
  right: 0;
  top: 100%;
  margin-top: 2px;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.12);
  max-height: 260px;
  overflow-y: auto;
  z-index: 41;
}
.filter-option {
  display: flex;
  align-items: center;
  padding: 4px 0;
  font-size: 0.95em;
  cursor: pointer;
}
.filter-option input {
  margin: 0 8px 0 0;
  flex-shrink: 0;
}
.filter-picker-actions {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #eee;
}
.filter-picker-actions .filter-clear {
  font-size: 0.9em;
  padding: 4px 10px;
  cursor: pointer;
  background: #f5f5f5;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
}

.dropdown-item, .dropdown .dropdown-menu .has-link a {
  font-size: 1em;
}

.modal-card .datepicker .dropdown-menu {
  position: fixed;
  top: 33% !important;
  left: 24% !important;
  overflow-y: auto;
}

.field:not(:last-child) {
  margin-bottom: 1.25rem;
}

ul.location-list {
  list-style: square inside;
  margin-left: 2em;
}

ul.location-list li {
  margin-bottom: 0.5em;
}

span.delete {
  margin-top: 4px;
  margin-left: 5px;
}

.left-spacer-5 {
  margin-left: 5px;
}

.left-spacer-10 {
  margin-left: 10px;
}

.left-spacer-20 {
  margin-left: 20px;
}

.left-spacer-30 {
  margin-left: 30px;
}

.top-spacer-10 {
  margin-top: 10px;
}

.top-spacer-20 {
  margin-top: 20px;
}

.right-spacer-40 {
  margin-right: 40px;
}

.bottom-spacer-20 {
  margin-bottom: 20px;
}

li {
  margin: 5px 0 5px 40px;
}

.thumbnail {
  min-width: 64px;
  max-width: 64px;
  min-height: 64px;
  max-height: 64px;
  vertical-align: bottom;
}

.card-header {
  font-size: 1.25rem;
}

.group {
  background-color: #f5f5f5;
}

.date-picker {
  width: 150px;
}

.date-range-picker {
  width: 250px;
}

.datepicker .dropdown-content {
  min-width: 300px;
}

.schedules-container {
  width: 100%;
  margin-left: auto;
  margin-right: auto;
}

.schedule {
  background-color: #eee;
  box-shadow: 3px 3px 10px #ccc;
  padding: 20px;
  margin-bottom: 20px;
}

.campaign-edit .allowed-filters > .filter-field {
  display: flex;
  flex-direction: column;
  min-width: 0;
  margin: 0;
}

.campaign-edit .allowed-filters > .filter-field > .label {
  margin-bottom: 0.5em;
}

.campaign-edit .allowed-filters .input,
.campaign-edit .allowed-filters .filter-picker-toggle {
  height: 2.5em;
  min-height: 2.5em;
  box-sizing: border-box;
}

.campaign-edit .redirect-device-row > .field {
  margin: 0 0 1.25rem;
}

.campaign-edit .status-dates {
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
}

.campaign-edit .status-dates > .field {
  margin: 0 0 0.75rem;
  min-width: 0;
  flex: 1 1 0;
}

.campaign-edit .status-dates > .field:first-child {
  flex: 0 0 auto;
}

.campaign-edit .status-dates .inline.control {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  width: 100%;
  min-width: 0;
}

.campaign-edit .status-dates .date-picker {
  width: auto;
  flex: 1 1 auto;
  min-width: 0;
}

.campaign-edit .field.is-missing > .label {
  color: #f94069;
}

.campaign-edit .field.is-missing .input,
.campaign-edit .field.is-missing .textarea,
.campaign-edit .field.is-missing select,
.campaign-edit .field.is-missing .taginput-container {
  border-color: #f94069;
  box-shadow: 0 0 0 1px #f94069;
}

.campaign-edit .field.is-missing .taginput .input {
  border-color: transparent;
  box-shadow: none;
}
</style>

<script>
import CampaignChecks from "../components/CampaignChecks";
import Attribution from "../components/Attribution";
import ErrorMessages from "../components/ErrorMessages";
import PIESetupForm from "../components/PIESetupForm";
import CreativeDisplay from "../components/CreativeDisplay";
import common from '../common'
import session from '../session'
import BField from "buefy/src/components/field/Field";
import debounce from 'lodash/debounce'

session.checkSession();
const offerId = session.getParam('offer_id');

// Mirrors demandscale-api BillingTypeEnum
const BillingTypeEnum = {
  CPI: { id: 1 },
  CPA: { id: 4 },
  CPM: { id: 6 }
};

const model = {
  isLoadingOrSaving: false,
  advancedTargetingOpen: false,
  maximumCpmAdvancedOpen: false,
  impressionTypeMaximumCpms: {},
  pieSectionOpen: false,
  openBasics: true,
  openTargeting: true,
  openBudget: true,
  openTracking: true,
  openCreatives: true,
  expandedCreativeGroups: {},
  openOptimize: true,
  openAdmin: true,
  budgetMoreOpen: false,
  isSuperUser: false,
  showSaveMessage: false,
  utcTipOpen: false,
  utcTipStyle: {},
  saveMessageOpacity: 0,
  BillingTypeEnum,
  billingTypes: [
    {id: BillingTypeEnum.CPI.id, name: "CPI"},
    {id: BillingTypeEnum.CPM.id, name: "CPM"}
  ],
  platforms: [
    {id: 1, name: "Android"},
    {id: 2, name: "iOS"},
    {id: 3, name: "Connected TV"}
  ],
  ctvTarget: [
    {id: 0, name: "Both"},
    {id: 1, name: "Android"},
    {id: 2, name: "iOS"}
  ],
  statuses: [
    {id: 'active', name: "Active"},
    {id: 'paused', name: "Paused"},
    {id: 'pending', name: "Pending"},
    {id: 'deleted', name: "Deleted"}
  ],
  pauseFeedbackReasons: [
    {id: "technical_issue", name: "Technical issue"},
    {id: "campaign_not_tracking", name: "Campaign not tracking"},
    {id: "low_velocity", name: "Low velocity"},
    {id: "out_of_budget", name: "Out of budget"},
    {id: "negative_roi", name: "Negative ROI"},
    {id: "user_quality", name: "User quality"},
  ],
  daysOfWeek: [
    {id: 1, name: "Monday"},
    {id: 2, name: "Tuesday"},
    {id: 3, name: "Wednesday"},
    {id: 4, name: "Thursday"},
    {id: 5, name: "Friday"},
    {id: 6, name: "Saturday"},
    {id: 7, name: "Sunday"}
  ],
  countries: [],
  filteredCountries: [],
  location_query: '',
  location_type: "region",
  open_on_focus: true,
  clear_on_select: true,
  is_fetching: false,
  location_search_results: [],
  hasPendingCreatives: false,
  campaign: {},
  // Multi-select UI state (comma-separated strings still live on form for API/Everflow)
  impressionTypePickerOpen: false,
  selectedImpressionTypeIds: [],
  impressionTypeOptions: [
    {id: 1, name: 'Small Banner'},
    {id: 2, name: 'VAST Video'},
    {id: 3, name: 'Audio'},
    {id: 4, name: 'Small Native'},
    {id: 5, name: 'Native Video'},
    {id: 6, name: 'Large Banner'},
    {id: 7, name: 'Large Native'},
    {id: 8, name: 'MRAID Video'},
    {id: 9, name: 'Interstitial'},
    {id: 10, name: 'Carousel'},
    {id: 11, name: 'Small Carousel'},
    {id: 12, name: 'Small MRAID Video'},
    {id: 13, name: 'Large Carousel'},
    {id: 14, name: 'Connected TV'},
    {id: 15, name: 'Playable'},
    {id: 16, name: 'Rewarded MRAID Video'},
    {id: 17, name: 'Vungle Playable'},
    {id: 18, name: 'VAST NonRewarded Video'},
  ],
  exchangePickerOpen: false,
  selectedExchangeIds: [],
  appCategoryPickerOpen: false,
  selectedAppCategoryIds: [],
  appCategories: [],
  // Active RTB exchanges (ids from ExchangeEnum)
  exchangeOptions: [
    {id: 201, name: 'Smaato'},
    {id: 202, name: 'OpenX'},
    {id: 205, name: 'Google'},
    {id: 206, name: 'Fyber'},
    {id: 231, name: 'PubMatic'},
  ],
  form: {
    provider_id: null,
    target_impression_types: null,
    target_exchanges: null,
    target_carriers: null,
    target_device_manufacturer: null,
    manual_app_category_ids: null,
    min_os_version: null,
    max_os_version: null,
    offer_id: null,
    campaign_id: null,
    platform_id: 1,
    ctv_target: 0,
    app_identifier: null,
    attribution_provider: null,
    name: null,
    status: 'active',
    start_date: null,
    end_date: null,
    device_ifa_required: false,
    retargeting: false,
    has_problem: false,
    impression_overrun: false,
    clone_creatives: false,
    clone_blacklist: false,
    clone_whitelist: false,
    impression_url: null,
    click_url: null,
    countries: [],
    location_overrides: [],
    traffic_source_type: "0",
    payout: null,
    maximum_cpm: null,
    maximum_cpm_by_impression_type: null,
    max_daily_spend: null,
    cpa_cpi_arbitrage: true,
    cpa_cpm_arbitrage: false,
    use_dcpi: false,
    full_trust_mature_installs: null,
    arpu_cohort_days: null,
    target_cohort_roas: null,
    suggested_payout: null,
    impact: null,
    suggested_payout_recommendation_engine: null,
    billing_type_id: 1,
    post_install_events: [{
      id: null,
      event_id: null,
      event_token: null,
      event_name: null,
      target_percentage: null,
      event_value: null,
      event_budget: null
    }],
    target_post_install_event: null,
    total_budget: null,
    budget_type: "1",
    daily_install_cap: null,
    daily_impression_cap: null,
    hard_kpis: null,
    creatives: [],
    redirect_test_url: null,
    redirect_test_final_destination: null,
    app_match: null,
    active_check: null,
    attribution_url_check: null,
    initial_redirects_check: null,
    number_of_redirects_check: null,
    location_check: null,
    app_creative_check: null,
    campaign_creative_check: null,
    top_banner_sizes_check: null,
    missing_top_banner_sizes: [],
    video_creative_check: null,
    playable_creative_check: null,
    http_redirects_check: null,
    recent_conversions_check: null,
    strategy_check: null,
    total_budget_check: null,
    total_budget75_percent_check: null,
    daily_install_cap_check: null,
    pause_reason: null,
    pause_reason_note: null,
    impressions_today: null,
    clicks_today: null,
    installs_today: null,
    impressions_ltd: null,
    clicks_ltd: null,
    installs_ltd: null,
    revenue_ltd: null,
    revenue_mtd: null,
    pushed: false,
    client_side_redirects: 1
  },
  checkboxes: {
    creativeStatuses: []
  },
  filtered_creatives: [],
  selected_creative_status: 'active',
  pricing: {
    min_price: null,
    suggested_price: null,
  },
  video_pricing: {
    min_price: null,
    suggested_price: null,
  },
  languages: [],
  schedule_groups: [],
  schedule_groups_with_blank: [],
  used_schedule_groups: [],
  schedule_groups_errors: [],
  is_schedules_popup_visible: false,
  partnerLevel: 'standard',
  providers: [],
  errors: [],
  app_errors: [],
  scroll: session.getParam('scroll'),
  initialStatus: null
};

function applyCreativeFilter() {
  common.clearArray(model.filtered_creatives);
  for (let i = 0; i < model.form.creatives.length; i++) {
    let creative = model.form.creatives[i];
    if (model.selected_creative_status === 'active' && creative.active || model.selected_creative_status === 'inactive' && !creative.active || model.selected_creative_status === 'all') {
      model.filtered_creatives.push(creative);
    }
  }
  common.clearArray(model.used_schedule_groups);
  let usedGroupIds = [];
  for (let i = 0; i < model.filtered_creatives.length; i++) {
    let groupId = model.filtered_creatives[i].group_id;
    if (groupId != null && usedGroupIds.indexOf(groupId) < 0) {
      usedGroupIds.push(groupId);
    }
  }
  model.used_schedule_groups.push({
    id: null,
    group_name: "Default",
    schedules: []
  });
  for (let i = 0; i < model.schedule_groups.length; i++) {
    let group = model.schedule_groups[i];
    if (usedGroupIds.indexOf(group.id) >= 0) {
      model.used_schedule_groups.push(group);
    }
  }
}

function updatePricingData() {
  function updatePricingDataSuccessHandler(response) {
    model.pricing.min_price = Math.min(response.min_price, response.suggested_price);
    model.pricing.suggested_price = Math.max(response.min_price, response.suggested_price);
  }

  function updatePricingDataErrorHandler() {
    model.pricing.min_price = null;
    model.pricing.suggested_price = null;
  }

  function updateVideoPricingDataSuccessHandler(response) {
    model.video_pricing.min_price = Math.min(response.min_price, response.suggested_price);
    model.video_pricing.suggested_price = Math.max(response.min_price, response.suggested_price);
  }

  function updateVideoPricingDataErrorHandler() {
    model.video_pricing.min_price = null;
    model.video_pricing.suggested_price = null;
  }

  if (model.form.platform_id && model.form.countries && model.form.countries.length > 0) {
    const data = {
      platform_id: model.form.platform_id,
      billing_type_id: model.form.billing_type_id,
      is_video: false,
      countries: model.form.countries
    };
    session.post('pricing', data, updatePricingDataSuccessHandler, updatePricingDataErrorHandler, []);

    const dataVideo = {
      platform_id: model.form.platform_id,
      billing_type_id: model.form.billing_type_id,
      is_video: true,
      countries: model.form.countries
    };
    session.post('pricing', dataVideo, updateVideoPricingDataSuccessHandler, updateVideoPricingDataErrorHandler, []);
  }
}

function listConfigured(value) {
  return value != null && String(value).trim() !== '';
}

function advancedTargetingConfigured(form) {
  const hasOs = !!(form.min_os_version || form.max_os_version);
  return hasOs || listConfigured(form.target_exchanges) || listConfigured(form.target_carriers) || listConfigured(form.target_impression_types) || listConfigured(form.target_device_manufacturer) || listConfigured(form.manual_app_category_ids);
}

function budgetExtrasConfigured(form) {
  return listConfigured(form.daily_impression_cap) || listConfigured(form.hard_kpis) || listConfigured(form.max_daily_spend);
}

function postInstallEventsConfigured(form) {
  const events = form.post_install_events || [];
  return events.some(function (event) {
    return event && (event.event_id || event.event_name);
  });
}

function getOfferSuccessHandler(responseData) {
  model.isLoadingOrSaving = false;
  model.checkboxes.creativeStatuses = [];
  for (var i = 0; i < responseData.creatives.length; i++) {
    const creative = responseData.creatives[i];
    var found = false;
    for (var j = 0; j < model.checkboxes.creativeStatuses.length; j++) {
      if (model.checkboxes.creativeStatuses[j] === creative.creative_id) {
        found = true;
      }
    }
    if (creative.active && !found) {
      model.checkboxes.creativeStatuses.push(creative.creative_id);
    }
  }

  // save and remove meta-data from form data
  model.hasPendingCreatives = responseData.has_pending_creatives;
  delete responseData.hasPendingCreatives;
  if (responseData.start_date) {
    responseData.start_date = new Date(Date.parse(responseData.start_date));
  }
  if (responseData.end_date) {
    responseData.end_date = new Date(Date.parse(responseData.end_date));
  }
  // Deep copy so form does not update the current live campaign data reference
  model.campaign = JSON.parse(JSON.stringify(responseData));
  useTargetingFieldNames(responseData);
  model.form = responseData;
  // Keep billing type numeric so CPI/CPM label branches stay consistent (API/select can yield strings)
  if (model.form.billing_type_id != null && model.form.billing_type_id !== '') {
    model.form.billing_type_id = Number(model.form.billing_type_id);
  }
  // Default CPA arbitrage flags to on for offers that predate the encoded fields
  if (model.form.cpa_cpi_arbitrage == null) {
    model.form.cpa_cpi_arbitrage = true;
  }
  if (model.form.cpa_cpm_arbitrage == null) {
    model.form.cpa_cpm_arbitrage = false;
  }
  // Default use_dcpi to off for offers that predate the encoded field
  if (model.form.use_dcpi == null) {
    model.form.use_dcpi = false;
  }
  // use_dcpi is only valid when CPA CPM arbitrage is enabled
  if (!model.form.cpa_cpm_arbitrage) {
    model.form.use_dcpi = false;
    model.form.full_trust_mature_installs = null;
    model.form.arpu_cohort_days = null;
    model.form.target_cohort_roas = null;
  }
  loadSelectedImpressionTypesFromForm();
  loadSelectedExchangesFromForm();
  loadSelectedAppCategoriesFromForm();
  loadImpressionTypeMaximumCpmsFromForm();
  model.advancedTargetingOpen = advancedTargetingConfigured(model.form);
  model.pieSectionOpen = postInstallEventsConfigured(model.form);
  model.budgetMoreOpen = budgetExtrasConfigured(model.form);
  model.initialStatus = model.form.status;
  if (!model.form.post_install_events || model.form.post_install_events.length === 0) {
    model.form.post_install_events = [{
      event_id: null,
      event_name: null,
      event_token: null,
    }];
  }
  updatePricingData();
  applyCreativeFilter();
}

function getCountriesSuccessHandler(responseData) {
  model.countries = responseData.countries;
}

function getAppCategoriesSuccessHandler(responseData) {
  model.appCategories = responseData && responseData.categories ? responseData.categories : [];
}

function getAppCategoriesErrorHandler() {
  model.appCategories = [];
}

function getCountriesErrorHandler() {
  model.countries = [
    {
      country_code: "US",
      alpha3_code: "USA",
      country_name: "United States"
    }
  ];
}

function errorHandler() {
  model.isLoadingOrSaving = false;
}

function getLanguagesSuccessHandler(responseData) {
  common.clearArray(model.languages);
  for (let i = 0; i < responseData.length; i++) {
    model.languages.push(responseData[i]);
  }
}

function getLanguagesErrorHandler() {
  model.languages = [];
}

function resetScheduleGroups() {
  common.clearArray(model.schedule_groups);
  common.clearArray(model.schedule_groups_with_blank);
  common.clearArray(model.used_schedule_groups);
  model.schedule_groups_with_blank.push({
    id: null,
    group_name: " ",
    schedules: []
  });
}

function getScheduleGroupsSuccessHandler(responseData) {
  resetScheduleGroups();
  for (let i = 0; i < responseData.length; i++) {
    const group = responseData[i];
    for (let j = 0; j < group.schedules.length; j++) {
      group.schedules[j].dates[0] = new Date(Date.parse(group.schedules[j].dates[0]));
      group.schedules[j].dates[1] = new Date(Date.parse(group.schedules[j].dates[1]));
    }
    model.schedule_groups.push(group);
    model.schedule_groups_with_blank.push(group);
  }
}

function getScheduleGroupsErrorHandler() {
  resetScheduleGroups();
}

function loadScheduleGroups() {
  session.get('creative_schedule_groups', getScheduleGroupsSuccessHandler, getScheduleGroupsErrorHandler, []);
}

function getProvidersSuccessHandler(responseData) {
  common.clearArray(model.providers);
  for (let i = 0; i < responseData.length; i++) {
    model.providers.push(responseData[i]);
  }
  model.isSuperUser = model.providers && model.providers.length > 0;
}

function getProvidersErrorHandler() {
  model.providers = [];
}

function getpartnerLevelSuccessHandler(responseData) {
  model.partnerLevel = responseData;
}

function getPartnerLevelErrorHandler() {
  model.partnerLevel = 'standard';
}

function updateCreativeStatuses() {
  for (let i = 0; i < model.form.creatives.length; i++) {
    const creative = model.form.creatives[i];
    creative.active = model.checkboxes.creativeStatuses.includes(creative.creative_id);
  }
}

if (offerId) {
  model.isLoadingOrSaving = true;
  session.get('offer/' + offerId, getOfferSuccessHandler, errorHandler, model.errors);
}
session.get('countries', getCountriesSuccessHandler, getCountriesErrorHandler, model.errors);
session.get('app_categories', getAppCategoriesSuccessHandler, getAppCategoriesErrorHandler, []);
session.get('languages', getLanguagesSuccessHandler, getLanguagesErrorHandler, []);
loadScheduleGroups();
session.get('providers', getProvidersSuccessHandler, getProvidersErrorHandler, []);
session.get('partner_level', getpartnerLevelSuccessHandler, getPartnerLevelErrorHandler, []);

/**
 Note: Most validation is taking place in the form as HTML5 validation.  This validation
 is for what HTML5 doesn't cover
 */
const androidPackageNameRegex = /^([A-Za-z]{1}[A-Za-z\d_-]*\.)+[A-Za-z][A-Za-z\d_-]*$|^(http(s)?:\/\/)?play\.google\.com\/.+details\?id=([A-Za-z]{1}[A-Za-z\d_-]*\.)+[A-Za-z][A-Za-z\d_-]*/;
const iOsAppStoreIdRegex = /^[0-9]+$|^id[0-9]+$|^(http(s)?:\/\/)?apps\.apple\.com\/.+\/id[0-9]+(\?.+)?/;

function trimString(input) {
  if (input === null || input === undefined) return input;

  const val = String(input).trim();
  if (val === '') return null;

  return val;
}

function isValidOsVersion(input, type, errors) {
  const val = trimString(input);
  if (val === null || val === undefined) return val;

  if (!(typeof val === "string" && /^\d+\.\d+$/.test(val))) {
    errors.push(`INVALID_${type}_OS_VERSION`);
  }

  return val;
}

function normalizeStringSequence(input) {
  const val = trimString(input);
  if (val === null || val === undefined) return null;

  const commaSeparatedNumbersRegex = /^\d+(?:\s*,\s*\d+)*$/;
  if (!commaSeparatedNumbersRegex.test(val)) return undefined; // signals invalid

  // normalize: "1,  8 ,11" -> "1,8,11"
  return val.replace(/\s*,\s*/g, ',');
}

/** Parse "1,8,11" (or null) into string ids for checkbox v-model (HTML values are strings). */
function parseIdListString(input) {
  const normalized = normalizeStringSequence(input);
  if (!normalized) {
    return [];
  }
  return normalized.split(',').map(function (s) {
    return String(parseInt(s, 10));
  }).filter(function (s) {
    return s !== 'NaN';
  });
}

/** Join selected id list into a sorted comma-separated string (or null if empty). */
function joinIdListToString(ids) {
  if (!ids || ids.length === 0) {
    return null;
  }
  const sorted = ids
    .map(function (id) { return parseInt(id, 10); })
    .filter(function (n) { return !Number.isNaN(n) && n > 0; })
    .sort(function (a, b) { return a - b; });
  return sorted.length ? sorted.join(',') : null;
}

function applySelectedImpressionTypesToForm() {
  model.form.target_impression_types = joinIdListToString(model.selectedImpressionTypeIds);
}

/** The offer API still uses allowed_* keys. The form uses target_* . */
function useTargetingFieldNames(form) {
  if (!form) {
    return;
  }
  form.target_exchanges = form.target_exchanges != null ? form.target_exchanges : (form.allowed_exchanges != null ? form.allowed_exchanges : null);
  form.target_carriers = form.target_carriers != null ? form.target_carriers : (form.allowed_carriers != null ? form.allowed_carriers : null);
  form.target_impression_types = form.target_impression_types != null ? form.target_impression_types : (form.allowed_impression_types != null ? form.allowed_impression_types : null);
  delete form.allowed_exchanges;
  delete form.allowed_carriers;
  delete form.allowed_impression_types;
}

function offerRequestBody(form) {
  const body = Object.assign({}, form);
  body.allowed_exchanges = form.target_exchanges;
  body.allowed_carriers = form.target_carriers;
  body.allowed_impression_types = form.target_impression_types;
  delete body.target_exchanges;
  delete body.target_carriers;
  delete body.target_impression_types;
  return body;
}

function emptyImpressionTypeMaximumCpms() {
  const values = {};
  model.impressionTypeOptions.forEach(function (t) {
    values[t.id] = null;
  });
  return values;
}

/** Blank and 0 are unset: the campaign Maximum CPM applies, and nothing is stored. */
function isIgnoredImpressionTypeMaximumCpm(raw) {
  if (raw === null || raw === undefined || String(raw).trim() === '') {
    return true;
  }
  const price = Number(String(raw).trim());
  return Number.isFinite(price) && price === 0;
}

function loadImpressionTypeMaximumCpmsFromForm() {
  const values = emptyImpressionTypeMaximumCpms();
  const raw = model.form.maximum_cpm_by_impression_type;
  if (raw) {
    String(raw).split(',').forEach(function (pair) {
      const bits = pair.split(':');
      if (bits.length !== 2) {
        return;
      }
      const id = parseInt(bits[0].trim(), 10);
      if (!Object.prototype.hasOwnProperty.call(values, id)) {
        return;
      }
      const text = bits[1].trim();
      if (isIgnoredImpressionTypeMaximumCpm(text)) {
        return;
      }
      values[id] = text;
    });
  }
  model.impressionTypeMaximumCpms = values;
  model.maximumCpmAdvancedOpen = model.impressionTypeOptions.some(function (t) {
    return !isIgnoredImpressionTypeMaximumCpm(values[t.id]);
  });
}

function applyImpressionTypeMaximumCpmsToForm() {
  const parts = [];
  let invalid = false;
  model.impressionTypeOptions.forEach(function (t) {
    const raw = model.impressionTypeMaximumCpms[t.id];
    if (isIgnoredImpressionTypeMaximumCpm(raw)) {
      model.impressionTypeMaximumCpms[t.id] = null;
      return;
    }
    const text = String(raw).trim();
    const price = Number(text);
    if (!/^\d+(\.\d{1,2})?$/.test(text) || !Number.isFinite(price) || price < 0 || price > 100) {
      invalid = true;
      return;
    }
    parts.push(t.id + ':' + price.toFixed(2));
  });
  if (invalid) {
    model.errors.push('INVALID_MAXIMUM_CPM_BY_IMPRESSION_TYPE');
    return;
  }
  model.form.maximum_cpm_by_impression_type = parts.length ? parts.join(',') : null;
}

function applySelectedExchangesToForm() {
  model.form.target_exchanges = joinIdListToString(model.selectedExchangeIds);
}

function loadSelectedImpressionTypesFromForm() {
  model.selectedImpressionTypeIds = parseIdListString(model.form.target_impression_types);
}

function loadSelectedExchangesFromForm() {
  model.selectedExchangeIds = parseIdListString(model.form.target_exchanges);
}

function loadSelectedAppCategoriesFromForm() {
  model.selectedAppCategoryIds = parseIdListString(model.form.manual_app_category_ids);
}

function applySelectedAppCategoriesToForm() {
  const cpm = Number(model.form.billing_type_id) === BillingTypeEnum.CPM.id;
  const phone = Number(model.form.platform_id) !== 3;
  if (!cpm || !phone) {
    model.form.manual_app_category_ids = null;
    return;
  }
  model.form.manual_app_category_ids = joinIdListToString(model.selectedAppCategoryIds);
}

function multiSelectSummary(selectedIds, options) {
  const selected = (selectedIds || []).map(String);
  if (selected.length === 0) {
    return 'All';
  }
  const labels = options
    .filter(function (o) {
      return selected.indexOf(String(o.id)) >= 0;
    })
    .map(function (o) {
      return o.id + ': ' + o.name;
    });
  if (labels.length <= 3) {
    return labels.join(', ');
  }
  return labels.length + ' selected';
}

function validateSchedules() {
  common.clearArray(model.schedule_groups_errors);
  for (let i = 0; i < model.schedule_groups.length; i++) {
    let group = model.schedule_groups[i];
    if ((group.group_name === '' || group.group_name === 'New Schedule') && model.schedule_groups_errors.length === 0) {
      model.schedule_groups_errors.push("ENTER_A_SCHEDULE_NAME");
    }
    for (let j = 0; j < group.schedules.length; j++) {
      if (group.schedules[j].dates.length === 0 && model.schedule_groups_errors.length === 0) {
        model.schedule_groups_errors.push("SELECT_A_DATE_RANGE");
      }
      if (group.schedules[j].days_of_the_week.length === 0 && model.schedule_groups_errors.length === 0) {
        model.schedule_groups_errors.push("SELECT_DAYS_OF_WEEK");
      }
    }
  }
  return model.schedule_groups_errors.length === 0;
}

export default {
  props: {
    clone: Boolean
  },

  components: {
    BField,
    CampaignChecks,
    Attribution,
    PIESetupForm,
    CreativeDisplay,
    ErrorMessages
  },

  data() {
    if (!model.impressionTypeMaximumCpms || Object.keys(model.impressionTypeMaximumCpms).length === 0) {
      model.impressionTypeMaximumCpms = emptyImpressionTypeMaximumCpms();
    }
    return model;
  },

  filters: {
    toCurrency: function (value) {
      if (value === undefined || value === null || Number.isNaN(value)) {
        return value;
      }
      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
      return formatter.format(value);
    },
    toPercent: function (value) {
      if (value === undefined || value === null || Number.isNaN(value)) {
        return value;
      }
      const formatter = new Intl.NumberFormat('en-US', {
        style: 'percent',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
      return formatter.format(value);
    },
    toNumber: function (value) {
      if (value === undefined || value === null || Number.isNaN(value)) {
        return value;
      }
      const formatter = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
      return formatter.format(value);
    }
  },

  computed: {
    // Coerce so string "1" from <select>/API still matches CPI id 1
    isCpiBilling: function () {
      return Number(this.form.billing_type_id) === BillingTypeEnum.CPI.id;
    },
    creativeTypes: function () {
      return common.unique(model.form.creatives.map(c => c.creative_type));
    },
    spentBudget: function () {
      if (model.form.budget_type === '2' || model.form.budget_type === 2) {
        if (model.form.revenue_mtd === undefined || model.form.revenue_mtd === null || model.form.revenue_mtd === '') {
          return 0;
        } else {
          return model.form.revenue_mtd;
        }
      } else {
        if (model.form.revenue_ltd === undefined || model.form.revenue_ltd === null || model.form.revenue_ltd === '') {
          return 0;
        } else {
          return model.form.revenue_ltd;
        }
      }
    },
    scheduleGroupsCompleted: function () {
      var completed = true;
      if (model.schedule_groups) {
        for (let i = 0; i < model.schedule_groups.length; i++) {
          let group = model.schedule_groups[i];
          if (group.group_name === '') {
            completed = false;
          }
          for (let j = 0; j < group.schedules.length; j++) {
            if (group.schedules[j].dates.length === 0) {
              completed = false;
            }
          }
        }
      }
      return completed;
    },
    impressionTypeSummary: function () {
      return multiSelectSummary(model.selectedImpressionTypeIds, model.impressionTypeOptions);
    },
    exchangeSummary: function () {
      return multiSelectSummary(model.selectedExchangeIds, model.exchangeOptions);
    },
    showManualAppCategories: function () {
      return Number(model.form.billing_type_id) === BillingTypeEnum.CPM.id && Number(model.form.platform_id) !== 3;
    },
    platformAppCategories: function () {
      const platformId = Number(model.form.platform_id);
      return (model.appCategories || []).filter(function (category) {
        return Number(category.platform_id) === platformId;
      });
    },
    appCategorySummary: function () {
      const selected = (model.selectedAppCategoryIds || []).map(String);
      if (!selected.length) {
        return this.$t('auto_target');
      }
      const labels = this.platformAppCategories
        .filter(function (category) {
          return selected.indexOf(String(category.id)) >= 0;
        })
        .map(function (category) {
          return category.name;
        });
      if (!labels.length) {
        return selected.length + ' selected';
      }
      if (labels.length <= 3) {
        return labels.join(', ');
      }
      return labels.length + ' selected';
    },
    /** Deep link to Reports with this app + campaign and Creative ID as third grouping. */
    platformName: function () {
      const platform = model.platforms.find(function (item) {
        return item.id === Number(model.form.platform_id);
      });
      return platform ? platform.name : '';
    },
    ecpiLtd: function () {
      const installs = Number(model.form.installs_ltd);
      const revenue = Number(model.form.revenue_ltd);
      if (!installs || !isFinite(revenue)) {
        return null;
      }
      return revenue / installs;
    },
    cvrLtd: function () {
      const installs = Number(model.form.installs_ltd);
      const connectedTv = Number(model.form.platform_id) === 3;
      const denominator = connectedTv ? Number(model.form.impressions_ltd) : Number(model.form.clicks_ltd);
      if (!installs || !denominator) {
        return null;
      }
      return installs / denominator;
    },
    payoutBelowSuggestion: function () {
      const payout = Number(model.form.payout);
      if (!payout) {
        return false;
      }
      const candidates = [model.form.suggested_payout, model.pricing.suggested_price, model.video_pricing.suggested_price];
      return candidates.some(function (value) {
        const suggested = Number(value);
        return isFinite(suggested) && suggested > payout;
      });
    },
    campaignReportUrl: function () {
      if (model.form.campaign_id !== undefined && model.form.campaign_id !== null && model.form.app_identifier) {
        return 'reports.html?app_identifier=' + encodeURIComponent(model.form.app_identifier);
      }
      return 'reports.html';
    },
    pieReportUrl: function () {
      if (model.form.campaign_id !== undefined && model.form.campaign_id !== null) {
        return 'post_install_reports.html?campaign_id=' + model.form.campaign_id;
      }
      return 'post_install_reports.html';
    },
    locationTargetingHelp: function () {
      return this.$t('narrow_down_targeting_tooltip') + ' ' + this.$t('narrow_down_targeting_warning');
    },
    basicsSummary: function () {
      const parts = [];
      if (this.platformName) {
        parts.push(this.platformName);
      }
      if (model.form.status) {
        parts.push(model.form.status);
      }
      const payout = Number(model.form.payout);
      if (payout) {
        parts.push(this.$options.filters.toCurrency(payout));
      }
      return parts.join(' · ');
    },
    targetingSummary: function () {
      const parts = [];
      const countries = model.form.countries || [];
      if (countries.length) {
        const names = countries.slice(0, 3).map(function (country) {
          return country.country_name || country.country_code;
        });
        let label = names.join(', ');
        if (countries.length > 3) {
          label += ' +' + (countries.length - 3);
        }
        parts.push(label);
      }
      const source = String(model.form.traffic_source_type);
      if (source === '1') {
        parts.push(this.$t('in_app'));
      } else if (source === '2') {
        parts.push(this.$t('mobile_web'));
      } else if (source === '0') {
        parts.push(this.$t('both'));
      }
      if (listConfigured(model.form.target_device_manufacturer)) {
        parts.push(String(model.form.target_device_manufacturer).trim());
      }
      if (this.showManualAppCategories && model.selectedAppCategoryIds.length) {
        parts.push(this.appCategorySummary);
      }
      return parts.join(' · ');
    },
    budgetSummary: function () {
      const budget = model.form.total_budget;
      if (budget === undefined || budget === null || budget === '') {
        return this.$t('unlimited');
      }
      const remaining = Number(budget) - Number(this.spentBudget);
      const value = remaining > 0 ? remaining : 0;
      return this.$t('remaining_budget') + ' ' + this.$options.filters.toCurrency(value);
    },
    trackingSummary: function () {
      return model.form.attribution_provider || '';
    },
    optimizeSummary: function () {
      const events = (model.form.post_install_events || []).filter(function (event) {
        return event && (event.event_id || event.event_name);
      });
      const parts = [];
      if (events.length) {
        parts.push(events.length + ' ' + this.$t('post_install_events_section'));
      } else {
        parts.push(this.$t('post_install_events_section'));
      }
      if (model.form.offer_id && !this.clone) {
        parts.push(this.$t('optimize_sources'));
      }
      return parts.join(' · ');
    },
    creativesSummary: function () {
      const creatives = model.form.creatives || [];
      const active = creatives.filter(function (creative) {
        return creative.active;
      }).length;
      return active + ' ' + this.$t('active');
    },
    adminSummary: function () {
      const providers = model.providers || [];
      const provider = providers.find(function (item) {
        return item.provider_id === model.form.provider_id;
      });
      return provider ? (provider.provider_id + ': ' + provider.name) : '';
    },
    creativeReportUrl: function () {
      const params = [];
      if (this.form.app_identifier) {
        params.push('app_identifier=' + encodeURIComponent(this.form.app_identifier));
      }
      if (this.form.campaign_id != null) {
        params.push('campaign_id=' + this.form.campaign_id);
      }
      // Single-campaign reports lock primary group-by to campaign; date enables third grouping
      params.push('group_by=campaign');
      params.push('second_group_by=date');
      params.push('third_group_by=creative_id');
      return params.length ? ('reports.html?' + params.join('&')) : 'reports.html';
    },
  },

  methods: {
    showUtcTip: function (event) {
      this._utcTipEl = event.currentTarget;
      this.moveUtcTip();
      this.utcTipOpen = true;
      window.addEventListener('scroll', this.moveUtcTip, true);
      window.addEventListener('resize', this.moveUtcTip);
    },

    hideUtcTip: function () {
      this.utcTipOpen = false;
      window.removeEventListener('scroll', this.moveUtcTip, true);
      window.removeEventListener('resize', this.moveUtcTip);
    },

    moveUtcTip: function () {
      const el = this._utcTipEl;
      if (!el) {
        return;
      }
      const rect = el.getBoundingClientRect();
      this.utcTipStyle = {
        left: (rect.left + rect.width / 2) + 'px',
        top: rect.top + 'px'
      };
    },

    onCpaCpmArbitrageChange: function (checked) {
      // use_dcpi requires CPA CPM arbitrage; clear it when CPM arbitrage is turned off
      if (!checked) {
        model.form.use_dcpi = false;
        model.form.full_trust_mature_installs = null;
        model.form.arpu_cohort_days = null;
        model.form.target_cohort_roas = null;
      }
    },

    toggleImpressionTypePicker: function () {
      if (model.initialStatus === 'deleted' && !this.$props.clone) {
        return;
      }
      model.exchangePickerOpen = false;
      model.appCategoryPickerOpen = false;
      model.impressionTypePickerOpen = !model.impressionTypePickerOpen;
    },

    syncAllowedImpressionTypesString: function () {
      applySelectedImpressionTypesToForm();
    },

    clearImpressionTypes: function () {
      model.selectedImpressionTypeIds = [];
      applySelectedImpressionTypesToForm();
    },

    toggleExchangePicker: function () {
      if (model.initialStatus === 'deleted' && !this.$props.clone) {
        return;
      }
      model.impressionTypePickerOpen = false;
      model.appCategoryPickerOpen = false;
      model.exchangePickerOpen = !model.exchangePickerOpen;
    },

    toggleAppCategoryPicker: function () {
      if (model.initialStatus === 'deleted' && !this.$props.clone) {
        return;
      }
      model.impressionTypePickerOpen = false;
      model.exchangePickerOpen = false;
      model.appCategoryPickerOpen = !model.appCategoryPickerOpen;
    },

    syncManualAppCategoriesString: function () {
      applySelectedAppCategoriesToForm();
    },

    clearAppCategories: function () {
      model.selectedAppCategoryIds = [];
      applySelectedAppCategoriesToForm();
    },

    syncAllowedExchangesString: function () {
      applySelectedExchangesToForm();
    },

    clearExchanges: function () {
      model.selectedExchangeIds = [];
      applySelectedExchangesToForm();
    },

    addTargetedLocation: function (location) {
      if (location) {
        var found = false;
        for (let i = 0; i < model.form.location_overrides.length; i++) {
          if (model.form.location_overrides[i].name === location.name) {
            found = true;
            break;
          }
        }
        if (!found) {
          model.form.location_overrides.push(location);
        }
      }
    },

    deleteLocationOverride: function (index) {
      if (index < model.form.location_overrides.length) {
        model.form.location_overrides.splice(index, 1);
      }
    },

    getFilteredCountries(text) {
      model.filteredCountries = model.countries.filter((country) => {
        return country.country_name.toLowerCase().indexOf(text.toLowerCase()) >= 0 || country.country_code.toLowerCase().indexOf(text.toLowerCase()) >= 0 || country.alpha3_code.toLowerCase().indexOf(text.toLowerCase()) >= 0
      });
    },

    searchLocation: debounce(function (searchTerm) {
      if (!searchTerm) {
        common.clearArray(model.location_search_results);
        return;
      }
      model.is_fetching = true;

      function autoCompleteSuccessHandler(response) {
        common.clearArray(model.location_search_results);
        response.forEach((item) => model.location_search_results.push(item));
        model.is_fetching = false;
      }

      function autoCompleteErrorHandler() {
        common.clearArray(model.location_search_results);
        model.is_fetching = false;
      }

      let countries = '';
      for (let i = 0; i < model.form.countries.length; i++) {
        if (countries.length > 0) {
          countries = countries + ',';
        }
        countries = model.form.countries[i].country_code;
      }
      session.get(`location_search?location_type=${model.location_type}&countries=${countries}&search_term=${searchTerm}`, autoCompleteSuccessHandler, autoCompleteErrorHandler, []);
    }, 250),

    clearIgnoredImpressionTypeMaximumCpm: function (id) {
      if (isIgnoredImpressionTypeMaximumCpm(model.impressionTypeMaximumCpms[id])) {
        model.impressionTypeMaximumCpms[id] = null;
      }
    },

    validate: function () {
      common.clearArray(model.errors);
      this.clearMissingFieldMarks();
      // Ensure multi-select UI state is reflected on string fields before submit
      applySelectedImpressionTypesToForm();
      applySelectedExchangesToForm();
      applySelectedAppCategoriesToForm();
      applyImpressionTypeMaximumCpmsToForm();
      const normalizedImpTypes = normalizeStringSequence(model.form.target_impression_types);
      if (normalizedImpTypes === undefined) {
        model.errors.push("INVALID_ALLOWED_IMPRESSION_TYPES");
      } else {
        model.form.target_impression_types = normalizedImpTypes;
      }

      const normalizedExchanges = normalizeStringSequence(model.form.target_exchanges);
      if (normalizedExchanges === undefined) {
        model.errors.push('INVALID_ALLOWED_EXCHANGES');
      } else {
        model.form.target_exchanges = normalizedExchanges;
      }

      model.form.target_carriers = trimString(model.form.target_carriers);
      model.form.target_device_manufacturer = trimString(model.form.target_device_manufacturer);
      const manualCategories = model.form.manual_app_category_ids;
      if (manualCategories && String(manualCategories).length > 1024) {
        model.errors.push('INVALID_MANUAL_APP_CATEGORIES');
      }

      model.form.min_os_version = isValidOsVersion(model.form.min_os_version, 'MIN', model.errors);
      model.form.max_os_version = isValidOsVersion(model.form.max_os_version, 'MAX', model.errors);

      var packageRegex = androidPackageNameRegex;
      if (model.form.platform_id === 2) {
        packageRegex = iOsAppStoreIdRegex;
      }
      const appIdentifier = model.form.app_identifier == null ? '' : String(model.form.app_identifier);
      if ((model.form.platform_id === 1 || model.form.platform_id === 2) && appIdentifier && !appIdentifier.match(packageRegex)) {
        model.errors.push('NOT_A_VALID_PACKAGE_NAME');
      }
      if (appIdentifier.length > 75) {
        model.errors.push('PACKAGE_NAME_TOO_LONG');
      }
      if (model.form.total_budget && !document.getElementById('total_budget_field').checkValidity()) {
        model.errors.push('INVALID_TOTAL_BUDGET');
      }
      if (model.form.total_budget && (model.form.total_budget < model.form.payout * model.form.daily_install_cap)) {
        model.errors.push('TOTAL_BUDGET_LESS_THAN_DAILY_BUDGET');
      }
      if (model.form.total_budget && !this.$props.clone && model.form.status === 'active' && model.isSuperUser === false) {
        if ((model.form.budget_type === "2" || model.form.budget_type === 2) && model.form.total_budget < model.form.revenue_mtd) {
          model.errors.push('TOTAL_BUDGET_LESS_THAN_MONTHLY_SPEND');
        }
        if ((model.form.budget_type === "1" || model.form.budget_type === 1) && model.form.total_budget < model.form.revenue_ltd) {
          model.errors.push('TOTAL_BUDGET_LESS_THAN_TOTAL_SPEND');
        }
      }
      if (model.form.billing_type_id === BillingTypeEnum.CPI.id) {
        if (model.form.payout && !document.getElementById('cost_per_install_field').checkValidity()) {
          model.errors.push('INVALID_COST_PER_INSTALL');
        }
        if (model.form.daily_install_cap && !document.getElementById('daily_install_cap_field').checkValidity()) {
          model.errors.push('INVALID_DAILY_INSTALL_CAP');
        }
      } else if (model.form.billing_type_id === BillingTypeEnum.CPM.id) {
        if (model.form.payout && !document.getElementById('cost_per_mile_field').checkValidity()) {
          model.errors.push('INVALID_COST_PER_MILE');
        }
        if (model.form.maximum_cpm && !document.getElementById('maximum_cpm_field').checkValidity()) {
          model.errors.push('INVALID_MAXIMUM_CPM');
        }
        if (model.form.max_daily_spend && !document.getElementById('max_daily_spend_field').checkValidity()) {
          model.errors.push('INVALID_MAX_DAILY_SPEND');
        }
        if (model.form.full_trust_mature_installs && document.getElementById('full_trust_mature_installs_field') && !document.getElementById('full_trust_mature_installs_field').checkValidity()) {
          model.errors.push('INVALID_FULL_TRUST_MATURE_INSTALLS');
        }
        if (model.form.arpu_cohort_days && document.getElementById('arpu_cohort_days_field') && !document.getElementById('arpu_cohort_days_field').checkValidity()) {
          model.errors.push('INVALID_ARPU_COHORT_DAYS');
        }
        if (model.form.target_cohort_roas && document.getElementById('target_cohort_roas_field') && !document.getElementById('target_cohort_roas_field').checkValidity()) {
          model.errors.push('INVALID_TARGET_COHORT_ROAS');
        }
        if (model.form.daily_install_cap && !document.getElementById('daily_acquisition_cap_field').checkValidity()) {
          model.errors.push('INVALID_DAILY_ACQUISITION_CAP');
        }
      } else {
        if (model.form.payout && !document.getElementById('cost_per_acquisition_field').checkValidity()) {
          model.errors.push('INVALID_COST_PER_ACQUISITION');
        }
        if (model.form.daily_install_cap && !document.getElementById('daily_acquisition_cap_field').checkValidity()) {
          model.errors.push('INVALID_DAILY_ACQUISITION_CAP');
        }
      }
      const countriesMissing = !model.form.countries || model.form.countries.length === 0;
      const form = document.getElementById('campaignForm');
      const valid = form.checkValidity() && !countriesMissing;
      if (model.errors.length === 0 && !valid) {
        model.errors.push('ALL_FIELDS_ARE_REQUIRED');
      }
      this.markMissingRequiredFields(countriesMissing);
      if (model.errors.length > 0) {
        this.showSaveMessage = false;
        this.revealInvalidSections();
        this.$nextTick(function () {
          window.setTimeout(function () {
            const first = document.querySelector('#campaignForm .field.is-missing');
            if (first && first.scrollIntoView) {
              first.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 250);
        });
      }
      return model.errors.length === 0;
    },

    clearMissingFieldMarks: function () {
      const form = document.getElementById('campaignForm');
      if (!form) {
        return;
      }
      const marked = form.querySelectorAll('.is-missing');
      for (let i = 0; i < marked.length; i++) {
        marked[i].classList.remove('is-missing');
      }
    },

    markMissingRequiredFields: function (countriesMissing) {
      const form = document.getElementById('campaignForm');
      if (!form) {
        return;
      }
      const controls = form.querySelectorAll('input, select, textarea');
      for (let i = 0; i < controls.length; i++) {
        const control = controls[i];
        if (!control.willValidate || control.checkValidity()) {
          continue;
        }
        const field = control.closest('.field');
        if (field) {
          field.classList.add('is-missing');
        }
      }
      if (countriesMissing) {
        const countriesField = form.querySelector('.countries-field');
        if (countriesField) {
          countriesField.classList.add('is-missing');
        }
      }
    },

    revealInvalidSections: function () {
      model.openBasics = true;
      model.openTargeting = true;
      model.openBudget = true;
      model.openTracking = true;
      model.openOptimize = true;
      model.openAdmin = true;
      model.budgetMoreOpen = true;
      model.advancedTargetingOpen = true;
      model.maximumCpmAdvancedOpen = true;
      model.pieSectionOpen = true;
    },

    submit: function () {
      if (this.validate()) {
        model.isLoadingOrSaving = true;
        updateCreativeStatuses();
        // DemandScale is provider 267: force CPM to S2S so RTB can use legacy /click|/impression
        if (model.form.billing_type_id === BillingTypeEnum.CPM.id) {
          model.form.client_side_redirects = 0;
        } else {
          // Max daily spend and per-impression-type maximum CPM are CPM-only
          model.form.max_daily_spend = null;
          model.form.maximum_cpm_by_impression_type = null;
        }
        if (model.form.arpu_cohort_days === '' || model.form.arpu_cohort_days === undefined) {
          model.form.arpu_cohort_days = null;
        }
        if (model.form.target_cohort_roas === '' || model.form.target_cohort_roas === undefined) {
          model.form.target_cohort_roas = null;
        }
        if (this.$props.clone) {
          model.form.pause_reason = null;
          model.form.pause_reason_note = null;
          if (model.form.clone_creatives === false) {
            model.form.creatives = [];
          }
          session.post('offer', offerRequestBody(model.form), this.saveOfferSuccessHandler, this.errorHandler, model.errors)
        } else if (offerId) {
          session.put('offer/' + offerId, offerRequestBody(model.form), this.saveOfferSuccessHandler, this.errorHandler, model.errors)
        } else {
          session.post('offer', offerRequestBody(model.form), this.saveOfferSuccessHandler, this.errorHandler, model.errors)
        }
      }
    },

    inactivateAllCreatives: function () {
      common.clearArray(model.checkboxes.creativeStatuses);
      updateCreativeStatuses();
    },

    formatDateRange: function (dates) {
      if (dates.length === 2) {
        return this.formatDate(dates[0]) + ' - ' + this.formatDate(dates[1]);
      } else {
        return '';
      }
    },

    formatDate: function (date) {
      if (date instanceof String) {
        return date;
      } else {
        const year = '' + date.getFullYear();
        var month = '' + (date.getMonth() + 1);
        var day = '' + date.getDate();
        if (month.length < 2) {
          month = '0' + month;
        }
        if (day.length < 2) {
          day = '0' + day;
        }
        return year + '-' + month + '-' + day;
      }
    },

    clearStartDate: function () {
      model.form.start_date = null;
    },

    clearEndDate: function () {
      model.form.end_date = null;
    },

    showManageSchedulesPopup: function () {
      common.clearArray(model.schedule_groups_errors);
      if (model.schedule_groups.length === 0) {
        this.addScheduleGroup();
      }
      model.is_schedules_popup_visible = true;
    },

    hideSchedulesPopup: function () {
      model.is_schedules_popup_visible = false;
      applyCreativeFilter();
    },

    saveSchedules: function () {
      function saveScheduleGroupsSuccessHandler() {
        model.is_schedules_popup_visible = false;
        common.clearArray(model.schedule_groups_errors);
        loadScheduleGroups();
        applyCreativeFilter();
      }

      function saveScheduleGroupsErrorHandler() {
        document.getElementById('manageSchedulesBottom').scrollIntoView({behavior: "smooth"})
      }

      if (validateSchedules()) {
        let groups = [];
        for (let i = 0; i < model.schedule_groups.length; i++) {
          let group = {
            id: model.schedule_groups[i].id,
            group_name: model.schedule_groups[i].group_name,
            schedules: []
          };
          for (let j = 0; j < model.schedule_groups[i].schedules.length; j++) {
            let schedule = {
              id: model.schedule_groups[i].schedules[j].id,
              dates: [this.formatDate(model.schedule_groups[i].schedules[j].dates[0]), this.formatDate(model.schedule_groups[i].schedules[j].dates[1])],
              days_of_the_week: model.schedule_groups[i].schedules[j].days_of_the_week
            };
            group.schedules.push(schedule);
          }
          groups.push(group);
        }
        const data = {
          groups: groups
        }
        session.post('creative_schedule_groups', data, saveScheduleGroupsSuccessHandler, saveScheduleGroupsErrorHandler, model.schedule_groups_errors);
      }
    },

    addSchedule: function (group) {
      group.schedules.push({
        id: null,
        dates: [],
        days_of_the_week: [1, 2, 3, 4, 5, 6, 7]
      });
    },

    addScheduleGroup: function () {
      model.schedule_groups.push({
        id: null,
        group_name: 'New Schedule',
        schedules: [{
          id: null,
          dates: [],
          days_of_the_week: [1, 2, 3, 4, 5, 6, 7]
        }]
      })
    },

    scheduleCompleted: function (group) {
      var completed = true;
      for (let i = 0; i < group.schedules.length; i++) {
        if (group.schedules[i].dates.length === 0) {
          completed = false;
        }
        if (group.schedules[i].days_of_the_week.length === 0) {
          completed = false;
        }
      }
      return completed;
    },

    creativesForGroup: function (group) {
      const creatives = this.filterByGroupId(model.filtered_creatives, group.id);
      if (model.expandedCreativeGroups[group.id]) {
        return creatives;
      }
      return creatives.slice(0, 10);
    },

    toggleCreativeGroup: function (groupId) {
      this.$set(model.expandedCreativeGroups, groupId, !model.expandedCreativeGroups[groupId]);
    },

    filterByGroupId: function (creatives, groupId) {
      let filtered = [];
      for (let i = 0; i < creatives.length; i++) {
        if (creatives[i].group_id === groupId || creatives[i].group_id == null && groupId == null) {
          filtered.push(creatives[i]);
        }
      }
      return filtered;
    },

    addCreatives: function () {
      if (this.validate()) {
        model.isLoadingOrSaving = true;
        updateCreativeStatuses();
        session.put('offer/' + offerId, offerRequestBody(model.form), this.navigateToAddCreativesPage, this.errorHandler, model.errors)
      }
    },

    reviewCreatives: function () {
      if (this.validate()) {
        model.isLoadingOrSaving = true;
        updateCreativeStatuses();
        session.put('offer/' + offerId, offerRequestBody(model.form), this.navigateToReviewCreativesPage, this.errorHandler, model.errors)
      }
    },

    parseAppIdentifier: function () {
      function successHandler(responseData) {
        if (responseData.output) {
          model.form.app_identifier = responseData.output;
        }
      }

      const data = {
        input: model.form.app_identifier
      };
      session.post('parse_app_identifier', data, successHandler, errorHandler, []);
      const appData = {
        offer_id: model.form.offer_id,
        app_identifier: model.form.app_identifier
      };
      session.post('validate_app', appData, this.noopHandler, this.noopHandler, model.app_errors)
    },

    updatePlatform: function () {
      if (Number(model.form.platform_id) === 3) {
        model.form.attribution_provider = 'AppsFlyer';
        model.form.click_url = null;
        model.appCategoryPickerOpen = false;
        model.selectedAppCategoryIds = [];
        model.form.manual_app_category_ids = null;
      } else {
        const allowed = {};
        this.platformAppCategories.forEach(function (category) {
          allowed[String(category.id)] = true;
        });
        model.selectedAppCategoryIds = model.selectedAppCategoryIds.filter(function (id) {
          return allowed[String(id)];
        });
        applySelectedAppCategoriesToForm();
      }
      updatePricingData();
    },

    updatePricing: function () {
      // <select> values are often strings; normalize for === checks elsewhere
      if (model.form.billing_type_id != null && model.form.billing_type_id !== '') {
        model.form.billing_type_id = Number(model.form.billing_type_id);
      }
      if (Number(model.form.billing_type_id) !== BillingTypeEnum.CPM.id) {
        model.appCategoryPickerOpen = false;
        model.selectedAppCategoryIds = [];
        model.form.manual_app_category_ids = null;
      }
      updatePricingData();
      if (model.form.countries && model.form.countries.length > 0) {
        const countriesField = document.querySelector('#campaignForm .countries-field');
        if (countriesField) {
          countriesField.classList.remove('is-missing');
        }
      }
    },

    saveOfferSuccessHandler: function (responseData) {
      model.isLoadingOrSaving = false;
      model.initialStatus = model.form.status;
      if (this.$props.clone) {
        session.navigateToPage('dashboard.html');
      } else if (!offerId) {
        session.navigateToPage('add_creatives.html?offer_id=' + responseData.offer_id);
      } else {
        this.showSaveMessage = true;
        this.saveMessageOpacity = 1;

        setTimeout(() => {
          const fadeInterval = setInterval(() => {
            if (this.saveMessageOpacity > 0) {
              this.saveMessageOpacity -= 0.01;
            } else {
              clearInterval(fadeInterval);
              this.showSaveMessage = false;
            }
          }, 50);
        }, 5000);
      }
    },

    noopHandler: function () {
    },

    errorHandler: function () {
      model.isLoadingOrSaving = false;
      this.showSaveMessage = false;
    },

    navigateToAddCreativesPage: function () {
      session.navigateToPage('add_creatives.html?offer_id=' + model.form.offer_id);
    },

    navigateToReviewCreativesPage: function () {
      session.navigateToPage('review_creatives.html', `offer_id=${model.form.offer_id}`);
    },

    showPauseMessage: function () {
      return model.campaign.status === "active" && model.form.status !== "active";
    },

    askForPauseReason: function () {
      return model.campaign.status === "active" && (model.form.status === "paused" || model.form.status === "deleted");
    },

    filterCreatives: function () {
      applyCreativeFilter();
    },

    flipPushStatus: function (push) {
      if (push) {
        session.put('push_offer/' + model.form.offer_id, {}, this.noopHandler, this.noopHandler, [])
      } else {
        session.put('unpush_offer/' + model.form.offer_id, {}, this.noopHandler, this.noopHandler, [])
      }
    },
  },

  mounted: function () {
    if (model.scroll === 'true') {
      window.scrollTo(0, document.body.scrollHeight);
    }
    // Close filter pickers when clicking outside (like native <select>)
    this._closeFilterPickers = function (e) {
      if (!model.impressionTypePickerOpen && !model.exchangePickerOpen && !model.appCategoryPickerOpen) {
        return;
      }
      const picker = e && e.target && e.target.closest
        ? e.target.closest('.filter-picker')
        : null;
      if (!picker) {
        model.impressionTypePickerOpen = false;
        model.exchangePickerOpen = false;
        model.appCategoryPickerOpen = false;
      }
    };
    document.addEventListener('click', this._closeFilterPickers);
    document.addEventListener('keydown', this._onFilterPickerKeydown = function (e) {
      if (e.key === 'Escape') {
        model.impressionTypePickerOpen = false;
        model.exchangePickerOpen = false;
        model.appCategoryPickerOpen = false;
      }
    });
    const form = document.getElementById('campaignForm');
    this._clearMissingMark = function (event) {
      const control = event.target;
      if (!control || !control.closest) {
        return;
      }
      const field = control.closest('.field');
      if (!field || !field.classList.contains('is-missing') || field.classList.contains('countries-field')) {
        return;
      }
      const controls = field.querySelectorAll('input, select, textarea');
      for (let i = 0; i < controls.length; i++) {
        const candidate = controls[i];
        if (candidate.willValidate && !candidate.checkValidity()) {
          return;
        }
      }
      field.classList.remove('is-missing');
    };
    if (form) {
      form.addEventListener('input', this._clearMissingMark);
      form.addEventListener('change', this._clearMissingMark);
    }
  },

  beforeDestroy: function () {
    this.hideUtcTip();
    const form = document.getElementById('campaignForm');
    if (form && this._clearMissingMark) {
      form.removeEventListener('input', this._clearMissingMark);
      form.removeEventListener('change', this._clearMissingMark);
    }
    if (this._closeFilterPickers) {
      document.removeEventListener('click', this._closeFilterPickers);
    }
    if (this._onFilterPickerKeydown) {
      document.removeEventListener('keydown', this._onFilterPickerKeydown);
    }
  }
}
</script>
